import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "react-hot-toast";
import { Tooltip } from "react-tooltip";
import { BASE_URL, WEBSOCKET_HOST } from "../../config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faTimes,
  faSpinner,
  faChevronDown,
  faChevronUp,
  faSearch,
  faEye,
  faPaperclip,
  faSave,
  faPaperPlane,
  faTimesCircle,
  faCheckCircle,
  faExclamationCircle,
  faSyncAlt,
  faAngleDoubleLeft,
  faAngleLeft,
  faAngleRight,
  faAngleDoubleRight,
  faUndo,
  faEyeSlash,
  faCamera, // Added for Scan button
} from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import ClaimDetailsModal from "./components/ClaimDetailsModal";
import AttachFilesModal from "./components/AttachFilesModal";
import ConfirmSubmitModal from "./components/ConfirmSubmitModal";
import CustomScanITReference from "./CustomScanITReference";

// Possible Claim Statuses
const claimStatusStyles = {
  NULL: "bg-gray-100 text-gray-800",
  "For RTP": "bg-amber-100 text-amber-800",
  "For Processing": "bg-blue-100 text-blue-800",
  Processed: "bg-green-100 text-green-800",
  Extracted: "bg-yellow-100 text-yellow-800",
  Incomplete: "bg-red-100 text-red-800",
  Paid: "bg-purple-100 text-purple-800",
};

const EditClaimsBatch = () => {
  const navigate = useNavigate();
  const { batchNo } = useParams(); // Get batch_no from URL
  const [formData, setFormData] = useState({
    batch_no: "",
    batch_status: "Open",
    received_date: "",
    due_date: "",
    provider_code: "",
    provider_reference_no: "",
    soa_amount: "",
    accepted_by: "",
    actual_claim_count: 0,
    actual_claim_amount: 0,
    batch_type: "Claims",
    est_claim_count: 0,
    est_claim_amount: 0,
    created_by: "system_user", // Replace with auth user
    physician_code: null,
    remarks: "",
    soa_fileupload: "[]",
  });
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isProcessingText, setIsProcessingText] = useState("Updating Batch...");
  const [isStep1Visible, setIsStep1Visible] = useState(true);
  const [providers, setProviders] = useState([]);
  const [providerPaymentTerms, setProviderPaymentTerms] = useState(30);
  const [isBatchSaved, setIsBatchSaved] = useState(true); // Assume saved since we're editinggg
  const fileInputRef = useRef(null);
  const isMounted = useRef(true);
  const websocketRef = useRef(null);
  const wsReconnectAttemptsRef = useRef(0);
  const wsReconnectTimeoutRef = useRef(null);
  const wsShouldReconnectRef = useRef(true);
  const selectedClaimRef = useRef(null);

  // Track if there are unsaved changes
  const hasUnsavedChanges = useRef(false);

  // Search Claims State
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showingEntries, setShowingEntries] = useState("");
  const [filters, setFilters] = useState({
    loa_no: "",
    transaction_no: "",
    patient_name: "",
    coverage: "",
    claim_status: "",
  });
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });
  const [attachedClaims, setAttachedClaims] = useState([]);
  const [attachedSearchTerm, setAttachedSearchTerm] = useState("");
  const [attachedPage, setAttachedPage] = useState(1);
  const [attachedPerPage, setAttachedPerPage] = useState(10);
  const [isAttachFilesModalOpen, setIsAttachFilesModalOpen] = useState(false);
  const [selectedClaimForFiles, setSelectedClaimForFiles] = useState(null);
  const [claimFiles, setClaimFiles] = useState([]);
  const [pendingUploadIds, setPendingUploadIds] = useState([]);
  const [pendingSoaUploadIds, setPendingSoaUploadIds] = useState([]);
  const uploadIdToClaimRef = useRef({});
  const pendingSoaUploadsRef = useRef([]);
  const claimFileInputRef = useRef(null);
  const [isDraggingClaim, setIsDraggingClaim] = useState(false);
  const [claims, setClaims] = useState([]);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isSearchTableLoading, setIsSearchTableLoading] = useState(false);
  const [isProviderLoading, setIsProviderLoading] = useState(false);
  const pendingUploadsRef = useRef([]);

  //
  // const [claims, setClaims] = useState([]); // Claims in the batch
  // const [batchNo, setBatchNo] = useState(""); // Batch number
  const [scanning, setScanning] = useState(false); // New state for scanning status

  // Date Filters
  const [dateFilters, setDateFilters] = useState({
    date_from: "",
    date_to: "",
  });

  // Initialize the date_from and date_to
  // set the date_from to 12 months before the current date and date_to to today’s date
  useEffect(() => {
    const today = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(today.getMonth() - 12);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    setDateFilters({
      date_from: formatDate(threeMonthsAgo),
      date_to: formatDate(today),
    });
  }, []);

  const [selectedProviderName, setSelectedProviderName] = useState("");

  // Modal States
  const [isClaimDetailsModalOpen, setIsClaimDetailsModalOpen] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isSearchClaimsOpen, setIsSearchClaimsOpen] = useState(false);
  const [isAttachedClaimsOpen, setIsAttachedClaimsOpen] = useState(true);
  const [isWaitingForScan, setIsWaitingForScan] = useState(false);
  const [scanWaitingData, setScanWaitingData] = useState(null);
  const [wsStatus, setWsStatus] = useState("disconnected");
  const [rtpReasons, setRtpReasons] = useState([]);
  const [isRtpActionModalOpen, setIsRtpActionModalOpen] = useState(false);
  const [rtpModalMode, setRtpModalMode] = useState("mark");
  const [rtpTargetClaim, setRtpTargetClaim] = useState(null);
  const [rtpReasonCode, setRtpReasonCode] = useState("");
  const [rtpCancelReason, setRtpCancelReason] = useState("");
  const [rtpModalRemarks, setRtpModalRemarks] = useState("");
  const [rtpModalAttachments, setRtpModalAttachments] = useState([]);
  const [rtpModalUploadQueue, setRtpModalUploadQueue] = useState([]);
  const [isSubmittingRtpAction, setIsSubmittingRtpAction] = useState(false);
  const rtpModalFileInputRef = useRef(null);

  const allowedFileTypes = ["pdf", "jpg", "jpeg", "png"];
  const maxFileSizeMB = 10;
  const maxUploadLimit = 10;
  const maxRtpUploadLimit = 2;
  const phpFormatter = new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const sanitizeScanParam = (value) => {
    return String(value ?? "").replace(/\+/g, " ").trim();
  };

  const formatScanDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-CA");
  };

  const toDateTimeLocalValue = useCallback((value) => {
    const rawValue = String(value ?? "").trim();
    if (!rawValue) return "";

    const normalizedValue = rawValue.replace(" ", "T");
    const matchedDateTime = normalizedValue.match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/,
    );

    if (matchedDateTime) {
      return `${matchedDateTime[1]}-${matchedDateTime[2]}-${matchedDateTime[3]}T${matchedDateTime[4]}:${matchedDateTime[5]}`;
    }

    const parsedDate = new Date(normalizedValue);
    if (Number.isNaN(parsedDate.getTime())) return "";

    const year = String(parsedDate.getFullYear()).padStart(4, "0");
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const day = String(parsedDate.getDate()).padStart(2, "0");
    const hour = String(parsedDate.getHours()).padStart(2, "0");
    const minute = String(parsedDate.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hour}:${minute}`;
  }, []);

  const buildCustomScanUrl = (params) => {
    const query = Object.entries(params)
      .map(([key, value]) => `${key}=${encodeURIComponent(sanitizeScanParam(value))}`)
      .join("&");
    return `customscan://open?${query}`;
  };

  const resolveApiErrorMessage = (error, fallbackMessage) => {
    const message = error.response?.data?.message;
    if (!message) return fallbackMessage;
    if (typeof message === "string") return message;
    if (Array.isArray(message)) return message.join(", ");
    if (typeof message === "object") {
      return Object.values(message).flat().join(", ");
    }
    return fallbackMessage;
  };

  const fetchRtpReasons = useCallback(async () => {
    try {
      const response = await axios.get(`${BASE_URL}/claims/batch-rtp/reasons`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
        },
      });
      setRtpReasons(response.data?.data || []);
    } catch (error) {
      setRtpReasons([]);
    }
  }, []);

  const playConfirmSound = useCallback(() => {
    try {
      const audio = new Audio("/mixkit-software-interface-back-2575.wav");
      audio.volume = 0.9;
      audio.play().catch(() => {});
    } catch (error) {
      // ignore audio playback errors
    }
  }, []);

  const applyAckStyles = useCallback(() => {
    const popup = Swal.getPopup();
    if (!popup) return;
    let style = popup.querySelector("#ack-checkbox-style");
    if (!style) {
      style = document.createElement("style");
      style.id = "ack-checkbox-style";
      style.textContent = `
        .ack-checkbox {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 12px;
          border-radius: 10px;
          border: 1px solid #e5e7eb;
          background: #f8fafc;
        }
        .ack-checkbox input {
          transform: scale(1.2);
          margin-right: 6px;
        }
        .ack-checkbox.ack-pulse {
          background: #fff7ed;
          border-color: #fb923c;
          box-shadow: 0 0 0 2px rgba(251, 146, 60, 0.35);
          animation: ackPulse 1s ease-in-out 2;
        }
        @keyframes ackPulse {
          0% { box-shadow: 0 0 0 0 rgba(251, 146, 60, 0.55); }
          70% { box-shadow: 0 0 0 6px rgba(251, 146, 60, 0); }
          100% { box-shadow: 0 0 0 0 rgba(251, 146, 60, 0); }
        }
      `;
      popup.appendChild(style);
    }
    const checkbox = popup.querySelector(".swal2-checkbox");
    if (checkbox) {
      checkbox.classList.add("ack-checkbox");
    }
  }, []);

  const pulseAck = useCallback(() => {
    applyAckStyles();
    const popup = Swal.getPopup();
    const checkbox = popup?.querySelector(".swal2-checkbox");
    if (checkbox) {
      checkbox.classList.remove("ack-pulse");
      void checkbox.offsetWidth;
      checkbox.classList.add("ack-pulse");
    }
  }, [applyAckStyles]);

  // Table Columns
  const columns = [
    { key: "loa_no", label: "Loa No.", sortable: true },
    { key: "transaction_no", label: "Transaction No.", sortable: true },
    { key: "loa_amount", label: "LOA Amount", sortable: true },
    { key: "claim_amount", label: "Claim Amount", sortable: true },
    { key: "patient_name", label: "Patient Name", sortable: true },
    { key: "coverage", label: "Coverage", sortable: true },
    { key: "date", label: "Date", sortable: true },
    { key: "claim_status", label: "Claim Status", sortable: true },
    { key: "attachment_status", label: "Attachments", sortable: false },
    { key: "stale", label: "Stale?", sortable: true },
    { key: "actions", label: "Actions", sortable: false },
  ];

  // Fetch Batch Details on Mount
  // const fetchBatchDetails = useCallback(async () => {
  //   if (!isMounted.current) return;
  //   try {
  //     setIsProcessing(true);
  //     const response = await axios.get(
  //       `${BASE_URL}/claims/batches/${batchNo}`,
  //       {
  //         headers: {
  //           Authorization: `Bearer ${localStorage.getItem(
  //             "authentication/token"
  //           )}`,
  //         },
  //       }
  //     );
  //     const batch = response.data.data;
  //     setFormData({
  //       batch_no: batch.batch_no,
  //       batch_status: batch.batch_status,
  //       received_date: batch.received_date
  //         ? new Date(batch.received_date).toISOString().slice(0, 16)
  //         : "",
  //       due_date: batch.due_date ? batch.due_date : "",
  //       provider_code: batch.provider_code,
  //       provider_reference_no: batch.provider_reference_no,
  //       accepted_by: batch.accepted_by,
  //       actual_claim_count: batch.act_claim_count || 0,
  //       actual_claim_amount: batch.act_claim_amount || 0,
  //       batch_type: batch.batch_type,
  //       est_claim_count: batch.est_claim_count || 0,
  //       est_claim_amount: batch.est_claim_amount || 0,
  //       created_by: batch.created_by,
  //       physician_code: batch.physician_code,
  //       remarks: batch.remarks || "",
  //       soa_fileupload: batch.soa_fileupload || "[]",
  //     });
  //     setSelectedProviderName(batch.provider_name);
  //     setProviderPaymentTerms(batch.provider.payment_terms || 30);

  //     // Initialize SOA files
  //     const soaFiles = JSON.parse(batch.soa_fileupload || "[]").map((file) => ({
  //       file: { name: file.original_file_name },
  //       progress: 100,
  //       isUploading: false,
  //       uploadId: file.upload_id,
  //       signed_url: file.signed_url,
  //       error: null,
  //     }));
  //     setFiles(soaFiles);

  //     // Initialize attached claims
  //     setAttachedClaims(
  //       batch.claims.map((claim) => ({
  //         ...claim,
  //         attachedFiles: claim.attachedFiles.map((file) => ({
  //           file: { name: file.original_file_name },
  //           progress: 100,
  //           isUploading: false,
  //           uploadId: file.upload_id,
  //           signed_url: file.signed_url,
  //           error: null,
  //         })),
  //         amount: Number(claim.amount) || 0,
  //       }))
  //     );

  //     // Set date filters (default to 3 months prior to today)
  //     const today = new Date();
  //     const threeMonthsAgo = new Date();
  //     threeMonthsAgo.setMonth(today.getMonth() - 3);
  //     const formatDate = (date) =>
  //       `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
  //         2,
  //         "0"
  //       )}-${String(date.getDate()).padStart(2, "0")}`;
  //     setDateFilters({
  //       date_from: formatDate(threeMonthsAgo),
  //       date_to: formatDate(today),
  //     });
  //   } catch (error) {
  //     toast.error("Failed to fetch batch details.", { duration: 4000 });
  //     navigate("/claims/batches");
  //   } finally {
  //     setIsProcessing(false);
  //   }
  // }, [batchNo, navigate]);

  // Fetch Batch Details on Mount
  const fetchBatchDetails = useCallback(async () => {
    if (!isMounted.current) return;
    try {
      setIsProcessing(true);
      setIsProcessingText("Checking edit status...");

      // Check if batch is being edited
      const checkResponse = await axios.get(
        `${BASE_URL}/claims/check-edit-session/${batchNo}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token",
            )}`,
          },
        },
      );

      if (checkResponse.data.is_being_edited) {
        await Swal.fire({
          title: "Batch In Use",
          text: checkResponse.data.message,
          icon: "warning",
          confirmButtonColor: "#4f46e5",
          confirmButtonText: "Go to Claims Batch List",
        });
        navigate("/claims/batches");
        return;
      }

      // Start edit session
      await axios.post(
        `${BASE_URL}/claims/start-edit-session`,
        { batch_no: batchNo },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token",
            )}`,
          },
        },
      );

      // Fetch batch details
      const response = await axios.get(
        `${BASE_URL}/claims/batches/${batchNo}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token",
            )}`,
          },
        },
      );
      const batch = response.data.data;
      setFormData({
        batch_no: batch.batch_no,
        batch_status: batch.batch_status,
        received_date: toDateTimeLocalValue(batch.received_date),
        due_date: batch.due_date ? batch.due_date : "",
        provider_code: batch.provider_code,
        provider_reference_no: batch.provider_reference_no,
        soa_amount: batch.soa_amount ?? "",
        accepted_by: batch.accepted_by,
        actual_claim_count: batch.act_claim_count || 0,
        actual_claim_amount: batch.act_claim_amount || 0,
        batch_type: batch.batch_type,
        est_claim_count: batch.est_claim_count || 0,
        est_claim_amount: batch.est_claim_amount || 0,
        created_by: batch.created_by,
        physician_code: batch.physician_code,
        remarks: batch.remarks || "",
        soa_fileupload: batch.soa_fileupload || "[]",
      });
      setSelectedProviderName(batch.provider_name);
      setProviderPaymentTerms(batch.provider?.payment_terms || 30);

      // Initialize SOA files
      const soaFiles = JSON.parse(batch.soa_fileupload || "[]").map((file) => ({
        file: { name: file.original_file_name },
        progress: 100,
        isUploading: false,
        uploadId: file.upload_id,
        signed_url: file.signed_url,
        error: null,
      }));
      setFiles(soaFiles);

      // Initialize attached claims
      setAttachedClaims(
        batch.claims.map((claim) => {
          const isApprovedCancel =
            String(claim.rtp_cancel_status || "").toUpperCase() === "APPROVED";
          const computedRtpTag =
            !isApprovedCancel && (Boolean(claim.rtp_tag) || Boolean(claim.stale));

          return {
            ...claim,
            attachedFiles: (claim.attachedFiles || []).map((file) => ({
              file: { name: file.original_file_name },
              progress: 100,
              isUploading: false,
              uploadId: file.upload_id,
              signed_url: file.signed_url,
              error: null,
            })),
            amount: Number(claim.amount) || 0,
            claim_amount:
              claim.claim_amount === null ||
              claim.claim_amount === undefined ||
              claim.claim_amount === ""
                ? null
                : Number(claim.claim_amount),
            claim_status: computedRtpTag
              ? "For RTP"
              : claim.claim_status || null,
            rtp_tag: computedRtpTag,
            rtp_code: claim.rtp_code || (computedRtpTag && claim.stale ? "1" : ""),
            rtp_remarks:
              claim.rtp_remarks ||
              (computedRtpTag && claim.stale
                ? "Stale claim (auto-detected during batching)"
                : ""),
            rtp_cancel_status: claim.rtp_cancel_status || null,
            rtp_cancel_request_id: claim.rtp_cancel_request_id || null,
          };
        }),
      );

      // Set date filters
      const today = new Date();
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(today.getMonth() - 12);
      const formatDate = (date) =>
        `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
          2,
          "0",
        )}-${String(date.getDate()).padStart(2, "0")}`;
      setDateFilters({
        date_from: formatDate(threeMonthsAgo),
        date_to: formatDate(today),
      });
    } catch (error) {
      console.log(error);
      toast.error(
        error.response?.data?.message || "Failed to fetch batch details.",
        { duration: 4000 },
      );
      navigate("/claims/batches");
    } finally {
      setIsProcessing(false);
    }
  }, [batchNo, navigate, toDateTimeLocalValue]);

  // useEffect(() => {
  //   isMounted.current = true;
  //   fetchBatchDetails();
  //   return () => {
  //     isMounted.current = false;
  //   };
  // }, [fetchBatchDetails]);

  useEffect(() => {
    isMounted.current = true;
    fetchBatchDetails();
    return () => {
      isMounted.current = false;
      endEditSession();
    };
  }, [fetchBatchDetails]);

  // Old spec moved to For RTP Entry Module
  // useEffect(() => {
  //   fetchRtpReasons();
  // }, [fetchRtpReasons]);

  // Handle browser back/forward navigation
  useEffect(() => {
    // Push a state to history to detect back navigation
    window.history.pushState(null, null, window.location.href);

    const handlePopState = (event) => {
      if (hasUnsavedChanges.current) {
        // Show a custom confirmation
        const confirmLeave = window.confirm(
          "You have unsaved changes. Do you want to save your progress before leaving?",
        );

        if (confirmLeave) {
          // Optionally save progress here
          saveProgress(); // Your save function
          // Allow navigation
          window.history.back();
        } else {
          // Prevent navigation by pushing the state again
          window.history.pushState(null, null, window.location.href);
        }
      } else {
        // No unsaved changes, allow navigation
        window.history.back();
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // // Example function to simulate unsaved changes
  // const handleInputChange = () => {
  //   hasUnsavedChanges.current = true; // Set to true when user makes changes
  // };

  // // Example save function
  const saveProgress = () => {
    console.log("Saving progress...");
    hasUnsavedChanges.current = false; // Reset after saving
  };

  // Handle page unload (e.g., closing tab or navigating away)
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (hasUnsavedChanges.current) {
        // Show native browser prompt
        event.preventDefault();
        event.returnValue =
          "You have unsaved changes. Are you sure you want to leave?";
        return event.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    selectedClaimRef.current = selectedClaimForFiles;
  }, [selectedClaimForFiles]);

  useEffect(() => {
    pendingUploadsRef.current = pendingUploadIds;
  }, [pendingUploadIds]);

  useEffect(() => {
    pendingSoaUploadsRef.current = pendingSoaUploadIds;
  }, [pendingSoaUploadIds]);

  const pollUploadQueueStatus = useCallback(async () => {
    const uploadIds = pendingUploadsRef.current;
    if (!uploadIds.length) return;
    try {
      const response = await axios.post(
        `${BASE_URL}/claims/upload-queue/status`,
        { upload_ids: uploadIds },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token",
            )}`,
          },
        },
      );

      const rows = response.data?.data || [];
      const completedIds = [];

      rows.forEach((row) => {
        if (row.status === "success") {
          const uploadedFile = {
            file: { name: row.original_file_name || row.file_name },
            progress: 100,
            isUploading: false,
            uploadId: row.upload_id,
            signed_url: row.signed_url,
            error: null,
          };

          setAttachedClaims((prevClaims) =>
            prevClaims.map((claim) =>
              claim.transaction_no === row.transaction_no
                ? {
                    ...claim,
                    attachedFiles: [
                      ...(claim.attachedFiles || []),
                      uploadedFile,
                    ],
                  }
                : claim,
            ),
          );

          setClaimFiles((prevFiles) => {
            const currentClaim = selectedClaimRef.current;
            if (
              currentClaim &&
              currentClaim.transaction_no === row.transaction_no
            ) {
              return [...prevFiles, uploadedFile];
            }
            return prevFiles;
          });

          completedIds.push(row.upload_id);
        } else if (row.status === "failed") {
          toast.error(row.error_message || "Scanned file upload failed.", {
            duration: 4000,
          });
          completedIds.push(row.upload_id);
        }
      });

      if (completedIds.length > 0) {
        setPendingUploadIds((prev) =>
          prev.filter((id) => !completedIds.includes(id)),
        );
      }
    } catch (error) {
      // keep polling; avoid noisy toast
    }
  }, []);

  const pollSoaUploadQueueStatus = useCallback(async () => {
    const uploadIds = pendingSoaUploadsRef.current;
    if (!uploadIds.length) return;
    try {
      const response = await axios.post(
        `${BASE_URL}/claims/soa-upload-queue/status`,
        { upload_ids: uploadIds },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token",
            )}`,
          },
        },
      );

      const rows = response.data?.data || [];
      const completedIds = [];
      if (rows.length > 0) {
        setIsWaitingForScan(false);
        setScanWaitingData(null);
      }
      if (rows.length > 0) {
        setIsWaitingForScan(false);
        setScanWaitingData(null);
      }

      rows.forEach((row) => {
        if (row.status === "success") {
          const uploadedFile = {
            file: { name: row.original_file_name || row.file_name || "SOA" },
            progress: 100,
            isUploading: false,
            uploadId: row.upload_id,
            signed_url: row.signed_url,
            error: null,
          };

          setFiles((prev) => {
            if (prev.some((file) => file.uploadId === row.upload_id)) {
              return prev;
            }
            return [...prev, uploadedFile];
          });

          setFormData((prev) => {
            let existing = [];
            try {
              existing = JSON.parse(prev.soa_fileupload || "[]");
            } catch (error) {
              existing = [];
            }
            if (!existing.some((item) => item.uploadId === row.upload_id)) {
              existing.push({
                uploadId: row.upload_id,
                signed_url: row.signed_url || null,
                file_name: row.file_name || null,
              });
            }
            return { ...prev, soa_fileupload: JSON.stringify(existing) };
          });

          completedIds.push(row.upload_id);
        } else if (row.status === "failed") {
          toast.error(row.error_message || "SOA upload failed.", {
            duration: 4000,
          });
          completedIds.push(row.upload_id);
        }
      });

      if (completedIds.length > 0) {
        setPendingSoaUploadIds((prev) =>
          prev.filter((id) => !completedIds.includes(id)),
        );
      }
    } catch (error) {
      // keep polling; avoid noisy toast
    }
  }, []);

  const hydratePendingUploads = useCallback(async () => {
    if (!formData.batch_no) return;
    try {
      const response = await axios.get(
        `${BASE_URL}/claims/upload-queue/pending/${formData.batch_no}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token",
            )}`,
          },
        },
      );
      const rows = response.data?.data || [];
      if (rows.length === 0) return;
      setIsWaitingForScan(false);
      setScanWaitingData(null);

      setPendingUploadIds((prev) => {
        const merged = new Set(prev);
        rows.forEach((row) => {
          if (row.upload_id) {
            merged.add(row.upload_id);
          }
          if (row.upload_id && row.transaction_no) {
            uploadIdToClaimRef.current[row.upload_id] = row.transaction_no;
          }
        });
        return Array.from(merged);
      });
    } catch (error) {
      // ignore hydrate errors
    }
  }, [formData.batch_no]);

  const hydratePendingSoaUploads = useCallback(async () => {
    if (!formData.batch_no) return;
    try {
      const response = await axios.get(
        `${BASE_URL}/claims/soa-upload-queue/pending/${formData.batch_no}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token",
            )}`,
          },
        },
      );
      const rows = response.data?.data || [];
      if (rows.length === 0) return;
      setIsWaitingForScan(false);
      setScanWaitingData(null);

      setPendingSoaUploadIds((prev) => {
        const merged = new Set(prev);
        rows.forEach((row) => {
          if (row.upload_id) {
            merged.add(row.upload_id);
          }
        });
        return Array.from(merged);
      });
    } catch (error) {
      // ignore hydrate errors
    }
  }, [formData.batch_no]);

  useEffect(() => {
    if (pendingUploadIds.length === 0) return;
    pollUploadQueueStatus();
    const interval = setInterval(() => {
      pollUploadQueueStatus();
    }, 3000);
    return () => clearInterval(interval);
  }, [pendingUploadIds.length, pollUploadQueueStatus]);

  useEffect(() => {
    if (pendingSoaUploadIds.length === 0) return;
    pollSoaUploadQueueStatus();
    const interval = setInterval(() => {
      pollSoaUploadQueueStatus();
    }, 3000);
    return () => clearInterval(interval);
  }, [pendingSoaUploadIds.length, pollSoaUploadQueueStatus]);

  useEffect(() => {
    if (!isBatchSaved) return;
    hydratePendingUploads();
    hydratePendingSoaUploads();
  }, [isBatchSaved, hydratePendingUploads, hydratePendingSoaUploads]);

  useEffect(() => {
    wsShouldReconnectRef.current = true;

    const connectWebSocket = () => {
      const token = localStorage.getItem("authentication/token");
      if (!token || !WEBSOCKET_HOST) {
        return;
      }

      // const protocol = window.location.protocol === "https:" ? "wss" : "ws";
      const protocol = "wss";
      const wsUrl = `${protocol}://${WEBSOCKET_HOST}?token=${encodeURIComponent(
        token,
      )}`;
      const ws = new WebSocket(wsUrl);
      websocketRef.current = ws;

      ws.onopen = () => {
        wsReconnectAttemptsRef.current = 0;
        setWsStatus("connected");
      };

      ws.onmessage = (event) => {
        let message;
        try {
          message = JSON.parse(event.data);
        } catch (error) {
          return;
        }

        if (message.type !== "scan_upload_result") {
          return;
        }

        setIsWaitingForScan(false);
        setScanWaitingData(null);

        const uploadType = message.upload_type || "loa";
        const queueStatus =
          message.queue_status || (message.status === "queued" ? "queued" : null);
        const isQueued = queueStatus === "queued";

        if (uploadType === "soa") {
          if (isQueued) {
            const queuedFiles = message.files || [];
            if (queuedFiles.length > 0) {
              setPendingSoaUploadIds((prev) => [
                ...prev,
                ...queuedFiles.map((f) => f.upload_id).filter(Boolean),
              ]);
            }
            toast.success(message.message || "SOA files queued for upload.", {
              duration: 3000,
            });
            return;
          }
          if (message.status === "success") {
            const uploadedFiles = message.files || [];
            if (uploadedFiles.length > 0) {
              const soaFileRecords = uploadedFiles.map((file) => ({
                file: {
                  name: file.original_file_name || file.file_name || "SOA",
                },
                progress: 100,
                isUploading: false,
                uploadId: file.upload_id || null,
                signed_url: file.signed_url || null,
                error: null,
              }));
              setFiles((prev) => [...prev, ...soaFileRecords]);
              setFormData((prev) => {
                let existing = [];
                try {
                  existing = JSON.parse(prev.soa_fileupload || "[]");
                } catch (error) {
                  existing = [];
                }
                const merged = [...existing];
                uploadedFiles.forEach((file) => {
                  if (!file.upload_id) return;
                  if (merged.some((item) => item.uploadId === file.upload_id)) {
                    return;
                  }
                  merged.push({
                    uploadId: file.upload_id,
                    signed_url: file.signed_url || null,
                    file_name: file.file_name || null,
                  });
                });
                return { ...prev, soa_fileupload: JSON.stringify(merged) };
              });
            }
            toast.success(message.message || "SOA files uploaded successfully.", {
              duration: 3000,
            });
            return;
          }

          toast.error(message.message || "SOA upload failed.", {
            duration: 4000,
          });
          return;
        }

        if (isQueued) {
          const queuedFiles = message.files || [];
          if (queuedFiles.length > 0) {
            setPendingUploadIds((prev) => [
              ...prev,
              ...queuedFiles.map((f) => f.upload_id).filter(Boolean),
            ]);
            queuedFiles.forEach((file) => {
              if (file.upload_id) {
                uploadIdToClaimRef.current[file.upload_id] =
                  message.transaction_no;
              }
            });
          }
          toast.success(message.message || "Scanned files queued for upload.", {
            duration: 3000,
          });
          return;
        }

        if (message.status === "success") {
          toast.success(message.message || "Scanned files uploaded successfully.", {
            duration: 3000,
          });
          return;
        }

        toast.error(message.message || "Scanned file upload failed.", {
          duration: 4000,
        });
      };

      ws.onerror = () => {
        setWsStatus("reconnecting");
        toast.error("WebSocket connection error.", { duration: 4000 });
      };

      ws.onclose = () => {
        websocketRef.current = null;
        if (!wsShouldReconnectRef.current) return;
        setWsStatus("reconnecting");
        if (wsReconnectTimeoutRef.current) {
          clearTimeout(wsReconnectTimeoutRef.current);
        }
        const attempt = wsReconnectAttemptsRef.current + 1;
        wsReconnectAttemptsRef.current = attempt;
        const delay = Math.min(10000, 1000 * Math.pow(2, attempt));
        wsReconnectTimeoutRef.current = setTimeout(connectWebSocket, delay);
      };
    };

    connectWebSocket();

    return () => {
      wsShouldReconnectRef.current = false;
      setWsStatus("disconnected");
      if (wsReconnectTimeoutRef.current) {
        clearTimeout(wsReconnectTimeoutRef.current);
        wsReconnectTimeoutRef.current = null;
      }
      if (websocketRef.current) {
        websocketRef.current.close();
        websocketRef.current = null;
      }
    };
  }, []);

  // Update Claim Counts
  useEffect(() => {
    const claimAmount = attachedClaims.reduce(
      (sum, claim) => sum + (Number(claim.amount) || 0),
      0,
    );
    setFormData((prev) => ({
      ...prev,
      actual_claim_amount: claimAmount,
    }));
  }, [attachedClaims]);

  const filteredAttachedClaims = useMemo(() => {
    const term = attachedSearchTerm.trim().toLowerCase();
    if (!term) return attachedClaims;
    return attachedClaims.filter((claim) => {
      const values = [
        claim.loa_no,
        claim.transaction_no,
        claim.patient_name,
        claim.coverage,
        claim.claim_status,
      ];
      return values.some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(term),
      );
    });
  }, [attachedClaims, attachedSearchTerm]);

  const attachedTotalPages = Math.max(
    1,
    Math.ceil(filteredAttachedClaims.length / attachedPerPage),
  );
  const attachedStart = (attachedPage - 1) * attachedPerPage;
  const pagedAttachedClaims = filteredAttachedClaims.slice(
    attachedStart,
    attachedStart + attachedPerPage,
  );

  useEffect(() => {
    setAttachedPage(1);
  }, [attachedSearchTerm, attachedPerPage]);

  useEffect(() => {
    if (attachedPage > attachedTotalPages) {
      setAttachedPage(1);
    }
  }, [attachedPage, attachedTotalPages]);

  const uploadingClaimsSet = useMemo(() => {
    const set = new Set();
    pendingUploadIds.forEach((uploadId) => {
      const transNo = uploadIdToClaimRef.current[uploadId];
      if (transNo) {
        set.add(transNo);
      }
    });
    return set;
  }, [pendingUploadIds]);

  // Fetch providers
  const fetchProviders = async (search = "") => {
    try {
      setIsProviderLoading(true);
      const response = await axios.get(
        `${BASE_URL}/providers/for-batch-registration`,
        {
          params: { search },
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token",
            )}`,
          },
        },
      );
      setProviders(response.data.data);
    } catch (error) {
      toast.error("Failed to fetch providers.", { duration: 4000 });
    } finally {
      setIsProviderLoading(false);
    }
  };

  // Handle provider search
  const handleProviderSearch = (e) => {
    const value = e.target.value;
    if (!formData.received_date) {
      toast.error("Please enter Date Received before searching providers.", {
        duration: 4000,
      });
      return;
    }
    setSelectedProviderName(value);
    setFormData((prev) => ({ ...prev, provider_code: "" }));
    fetchProviders(value);
  };

  // Handle provider selection
  const handleProviderSelect = (provider) => {
    setFormData((prev) => ({ ...prev, provider_code: provider.provider_code }));
    setSelectedProviderName(provider.provider_name);
    setProviderPaymentTerms(provider.payment_terms || 30);
    setProviders([]);
    updateDueDate(formData.received_date, provider.payment_terms || 30);
  };

  // Update Due Date
  const updateDueDate = (receivedDate, paymentTerms) => {
    if (receivedDate) {
      const received = new Date(receivedDate);
      const dueDate = new Date(
        received.setDate(received.getDate() + paymentTerms),
      );
      setFormData((prev) => ({
        ...prev,
        due_date: dueDate.toISOString().split("T")[0],
      }));
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "received_date") {
        updateDueDate(value, providerPaymentTerms);
      }
      hasUnsavedChanges.current = true;
      return updated;
    });
  };

  // Handle SOA file selection
  const handleFileChange = (selectedFiles) => {
    const validFiles = Array.from(selectedFiles).filter((file) => {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      const isAllowedType = allowedFileTypes.includes(fileExtension);
      const isWithinSizeLimit = file.size <= maxFileSizeMB * 1024 * 1024;

      if (!isAllowedType) {
        toast.error(
          `File ${file.name} is not supported. Only ${allowedFileTypes.join(
            ", ",
          )} files allowed.`,
          { duration: 4000 },
        );
        return false;
      }

      if (!isWithinSizeLimit) {
        toast.error(
          `File ${file.name} exceeds ${maxFileSizeMB}MB size limit.`,
          {
            duration: 4000,
          },
        );
        return false;
      }

      return true;
    });

    if (files.length + validFiles.length > maxUploadLimit) {
      toast.error(
        `You can only upload up to ${maxUploadLimit} files at a time.`,
        {
          duration: 4000,
        },
      );
      return;
    }

    const newFiles = validFiles.map((file) => ({
      file,
      progress: 0,
      isUploading: false,
      uploadId: null,
      error: null,
      signed_url: null,
    }));

    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    newFiles.forEach((fileObj) => handleFileUpload(fileObj));
  };

  // Handle SOA file upload
  const handleFileUpload = async (fileObj) => {
    setFiles((prevFiles) =>
      prevFiles.map((f) =>
        f.file === fileObj.file ? { ...f, isUploading: true } : f,
      ),
    );

    try {
      const soaFileformData = new FormData();
      soaFileformData.append("file", fileObj.file);
      soaFileformData.append("batch_no", formData.batch_no);

      const response = await axios.post(
        `${BASE_URL}/claims/batch-header/upload-soa`,
        soaFileformData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token",
            )}`,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            setFiles((prevFiles) =>
              prevFiles.map((f) =>
                f.file === fileObj.file ? { ...f, progress } : f,
              ),
            );
          },
        },
      );

      const uploadedFile = response.data?.files?.[0] || {};
      const upload_id = uploadedFile.upload_id || response.data?.upload_id;
      const signed_url = uploadedFile.signed_url || response.data?.signed_url;
      const file_name = uploadedFile.file_name || response.data?.file_name;
      setFiles((prevFiles) =>
        prevFiles.map((f) =>
          f.file === fileObj.file
            ? {
                ...f,
                isUploading: false,
                progress: 100,
                uploadId: upload_id || null,
                signed_url,
              }
            : f,
        ),
      );
      setFormData((prev) => ({
        ...prev,
        soa_fileupload: JSON.stringify([
          ...JSON.parse(prev.soa_fileupload),
          { uploadId: upload_id, signed_url, file_name },
        ]),
      }));
      toast.success(`File ${fileObj.file.name} uploaded successfully!`, {
        duration: 4000,
      });
    } catch (error) {
      const errorMessage = resolveApiErrorMessage(
        error,
        "Failed to upload file.",
      );
      setFiles((prevFiles) =>
        prevFiles.map((f) =>
          f.file === fileObj.file
            ? { ...f, isUploading: false, error: errorMessage }
            : f,
        ),
      );
      toast.error(errorMessage, { duration: 4000 });
    }
  };

  // Handle SOA file removal
  const handleRemoveFile = async (fileObj) => {
    const result = await Swal.fire({
      title: "Remove File?",
      text: `Are you sure you want to remove ${fileObj.file.name}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#4f46e5",
      cancelButtonColor: "#ef4444",
      confirmButtonText: "Yes, remove it",
    });

    if (result.isConfirmed) {
      setIsProcessing(true);
      setIsProcessingText("Deleting attached files...");
      try {
        await axios.delete(
          `${BASE_URL}/claims/batch-header/upload-soa/${fileObj.uploadId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token",
              )}`,
            },
          },
        );
        setFiles((prevFiles) =>
          prevFiles.filter((f) => f.file !== fileObj.file),
        );
        setFormData((prev) => ({
          ...prev,
          soa_fileupload: JSON.stringify(
            JSON.parse(prev.soa_fileupload).filter(
              (f) => f.uploadId !== fileObj.uploadId,
            ),
          ),
        }));
        toast.success("File removed successfully.", { duration: 3000 });
        setIsProcessing(false);
        setIsProcessingText("Saving Batch...");
      } catch (error) {
        setIsProcessing(false);
        setIsProcessingText("Saving Batch...");
        toast.error("Failed to remove file.", { duration: 4000 });
      }
    }
  };

  // Handle view SOA file
  const handleViewFile = (fileObj) => {
    if (fileObj.signed_url) {
      window.open(fileObj.signed_url, "_blank");
    } else {
      toast.error("File URL not available.", { duration: 4000 });
    }
  };

  const handleRefreshLink = async (fileObj, scope = "claim") => {
    if (!fileObj?.uploadId) {
      toast.error("Missing upload ID for refresh.", { duration: 4000 });
      return;
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/claims/refresh-url`,
        { upload_id: fileObj.uploadId },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token",
            )}`,
          },
        },
      );

      const refreshedUrl =
        response.data?.signed_url ||
        response.data?.file_url ||
        response.data?.file?.signed_url;
      if (!refreshedUrl) {
        toast.error("Failed to refresh file link.", { duration: 4000 });
        return;
      }

      if (scope === "soa") {
        setFiles((prev) =>
          prev.map((file) =>
            file.uploadId === fileObj.uploadId
              ? { ...file, signed_url: refreshedUrl }
              : file,
          ),
        );
      } else {
        setClaimFiles((prev) =>
          prev.map((file) =>
            file.uploadId === fileObj.uploadId
              ? { ...file, signed_url: refreshedUrl }
              : file,
          ),
        );
        setAttachedClaims((prevClaims) =>
          prevClaims.map((claim) => ({
            ...claim,
            attachedFiles: (claim.attachedFiles || []).map((file) =>
              file.uploadId === fileObj.uploadId
                ? { ...file, signed_url: refreshedUrl }
                : file,
            ),
          })),
        );
      }

      toast.success("Link refreshed successfully.", { duration: 3000 });
    } catch (error) {
      const errorMessage = resolveApiErrorMessage(
        error,
        "Failed to refresh file link.",
      );
      toast.error(errorMessage, { duration: 4000 });
    }
  };

  // Handle claim file selection
  const handleClaimFileChange = (selectedFiles) => {
    const validFiles = Array.from(selectedFiles).filter((file) => {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      const isAllowedType = allowedFileTypes.includes(fileExtension);
      const isWithinSizeLimit = file.size <= maxFileSizeMB * 1024 * 1024;

      if (!isAllowedType) {
        toast.error(
          `File ${file.name} is not supported. Only ${allowedFileTypes.join(
            ", ",
          )} files allowed.`,
          { duration: 4000 },
        );
        return false;
      }

      if (!isWithinSizeLimit) {
        toast.error(
          `File ${file.name} exceeds ${maxFileSizeMB}MB size limit.`,
          { duration: 4000 },
        );
        return false;
      }

      return true;
    });

    if (claimFiles.length + validFiles.length > maxUploadLimit) {
      toast.error(
        `You can only upload up to ${maxUploadLimit} files at a time.`,
        { duration: 4000 },
      );
      return;
    }

    const newFiles = validFiles.map((file) => ({
      file,
      progress: 0,
      isUploading: false,
      uploadId: null,
      error: null,
      signed_url: null,
    }));

    setClaimFiles((prevFiles) => [...prevFiles, ...newFiles]);
    newFiles.forEach((fileObj) => handleClaimFileUpload(fileObj));
  };

  // Handle claim file upload
  const handleClaimFileUpload = async (fileObj) => {
    const transactionNo = selectedClaimForFiles?.transaction_no;
    if (!transactionNo) {
      toast.error("No transaction selected for upload.", { duration: 4000 });
      return;
    }

    setClaimFiles((prevFiles) =>
      prevFiles.map((f) =>
        f.file === fileObj.file ? { ...f, isUploading: true } : f,
      ),
    );

    try {
      const formData = new FormData();
      formData.append("file", fileObj.file);
      formData.append("transaction_no", transactionNo);

      const response = await axios.post(
        `${BASE_URL}/claims/attach-files`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token",
            )}`,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            setClaimFiles((prevFiles) =>
              prevFiles.map((f) =>
                f.file === fileObj.file ? { ...f, progress } : f,
              ),
            );
          },
        },
      );

      const uploadedFile = response.data?.files?.[0] || {};
      const upload_id = uploadedFile.upload_id || response.data?.upload_id;
      const signed_url = uploadedFile.signed_url || response.data?.signed_url;
      const file_name = uploadedFile.file_name || response.data?.file_name;
      const uploadedFileRecord = {
        file: { name: fileObj.file?.name || file_name || "Uploaded file" },
        progress: 100,
        isUploading: false,
        uploadId: upload_id || null,
        signed_url,
        error: null,
        original_file_name: fileObj.file?.name,
      };
      setClaimFiles((prevFiles) =>
        prevFiles.map((f) =>
          f.file === fileObj.file
            ? {
                ...f,
                ...uploadedFileRecord,
              }
            : f,
        ),
      );
      if (upload_id && transactionNo) {
        setAttachedClaims((prevClaims) =>
          prevClaims.map((claim) => {
            if (claim.transaction_no !== transactionNo) {
              return claim;
            }
            const existingFiles = claim.attachedFiles || [];
            if (existingFiles.some((file) => file.uploadId === upload_id)) {
              return claim;
            }
            return {
              ...claim,
              attachedFiles: [...existingFiles, uploadedFileRecord],
            };
          }),
        );
      }
      toast.success(`File ${fileObj.file.name} uploaded successfully!`, {
        duration: 4000,
      });
    } catch (error) {
      const errorMessage = resolveApiErrorMessage(
        error,
        "Failed to upload file.",
      );
      setClaimFiles((prevFiles) =>
        prevFiles.map((f) =>
          f.file === fileObj.file
            ? { ...f, isUploading: false, error: errorMessage }
            : f,
        ),
      );
      toast.error(errorMessage, { duration: 4000 });
    }
  };

  // Handle claim file removal
  const handleRemoveClaimFile = async (fileObj) => {
    if (!fileObj.uploadId) {
      setClaimFiles((prevFiles) =>
        prevFiles.filter((f) => f.file !== fileObj.file),
      );
      return;
    }

    const result = await Swal.fire({
      title: "Remove File?",
      text: `Are you sure you want to remove ${fileObj.file.name}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#4f46e5",
      cancelButtonColor: "#ef4444",
      confirmButtonText: "Yes, remove it",
    });

    if (result.isConfirmed) {
      setIsProcessing(true);
      setIsProcessingText("Deleting attached files...");
      try {
        await axios.delete(
          `${BASE_URL}/claims/attach-files/${fileObj.uploadId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token",
              )}`,
            },
          },
        );
        setClaimFiles((prevFiles) =>
          prevFiles.filter((f) => f.file !== fileObj.file),
        );
        setAttachedClaims((prevClaims) =>
          prevClaims.map((claim) =>
            claim.id === selectedClaimForFiles.id
              ? {
                  ...claim,
                  attachedFiles: claim.attachedFiles.filter(
                    (f) => f.uploadId !== fileObj.uploadId,
                  ),
                }
              : claim,
          ),
        );
        toast.success("File removed successfully.", { duration: 3000 });
        setIsProcessing(false);
        setIsProcessingText("Updating Batch...");
      } catch (error) {
        toast.error("Failed to remove file.", { duration: 4000 });
        setIsProcessing(false);
        setIsProcessingText("Updating Batch...");
      }
    }
  };

  // Handle view claim file
  const handleViewClaimFile = (fileObj) => {
    if (fileObj.signed_url) {
      window.open(fileObj.signed_url, "_blank");
    } else {
      toast.error("File URL not available.", { duration: 4000 });
    }
  };

  // Handle drag-and-drop for SOA
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = e.dataTransfer.files;
    handleFileChange(droppedFiles);
  };

  const handleChooseFile = () => {
    fileInputRef.current.click();
  };

  // Handle drag-and-drop for claim files
  const handleClaimDragOver = (e) => {
    e.preventDefault();
    setIsDraggingClaim(true);
  };

  const handleClaimDragLeave = () => {
    setIsDraggingClaim(false);
  };

  const handleClaimDrop = (e) => {
    e.preventDefault();
    setIsDraggingClaim(false);
    const droppedFiles = e.dataTransfer.files;
    handleClaimFileChange(droppedFiles);
  };

  // Add this function after handleChooseClaimFile
  const endEditSession = async () => {
    try {
      await axios.post(
        `${BASE_URL}/claims/end-edit-session`,
        { batch_no: batchNo },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token",
            )}`,
          },
        },
      );
      hasUnsavedChanges.current = false;
    } catch (error) {
      console.error("Failed to end edit session:", error);
      hasUnsavedChanges.current = true;
    }
  };

  const handleChooseClaimFile = () => {
    claimFileInputRef.current.click();
  };

  // Search Claims Logic
  const fetchClaims = async () => {
    if (!formData.provider_code) {
      return;
    }
    try {
      setIsSearchTableLoading(true);
      const claimStatusFilter =
        filters.claim_status === "NULL" ? "" : filters.claim_status;
      const response = await axios.post(
        `${BASE_URL}/claims/search`,
        {
          provider_code: formData.provider_code,
          date_from: dateFilters.date_from || undefined,
          date_to:
            dateFilters.date_to || new Date().toISOString().split("T")[0],
          search_term: searchTerm,
          page: currentPage,
          per_page: itemsPerPage,
          sort_by:
            sortConfig.key === "date"
              ? "trans_date"
              : sortConfig.key === "transaction_no"
                ? "trans_no"
                : "member_name",
          sort_direction: sortConfig.direction,
          filters: {
            loa_no: filters.loa_no,
            transaction_no: filters.transaction_no,
            patient_name: filters.patient_name,
            coverage: filters.coverage,
            claim_status: claimStatusFilter,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token",
            )}`,
          },
        },
      );
      setClaims(response.data.data);
      setTotalRecords(response.data.pagination.total_records);
      setTotalPages(response.data.pagination.total_pages);
      setShowingEntries(response.data.pagination.showing_entries);
    } catch (error) {
      toast.error(
        "Failed to fetch claims: " +
          (error.response?.data?.message || error.message),
        { duration: 4000 },
      );
    } finally {
      setIsSearchTableLoading(false);
    }
  };

  // Fetch claims when provider_code changes
  useEffect(() => {
    if (isBatchSaved) {
      fetchClaims();
    }
  }, [formData.provider_code, isBatchSaved]);

  // Fetch claims when search, pagination, sort, or filters change
  useEffect(() => {
    if (isBatchSaved) {
      fetchClaims();
    }
  }, [searchTerm, currentPage, itemsPerPage, sortConfig, filters, dateFilters]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const renderPageNumbers = () => {
    const pages = [];
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);
    if (currentPage <= 3) endPage = Math.min(5, totalPages);
    else if (currentPage >= totalPages - 2)
      startPage = Math.max(totalPages - 4, 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 rounded-lg text-sm transition-all duration-200 ${
            currentPage === i
              ? "bg-indigo-600 text-white"
              : "text-gray-700 hover:bg-indigo-100"
          }`}
        >
          {i}
        </button>,
      );
    }
    return pages;
  };

  const handleViewClaim = (claim) => {
    setSelectedClaim(claim);
    setIsClaimDetailsModalOpen(true);
  };

  // Check if Claim is Already Batched
  const checkIsAlreadyBatched = async (transaction_no) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/claims/is-already-batched/${transaction_no}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token",
            )}`,
          },
        },
      );
      // return response.data.is_batched;
      return response.data;
    } catch (error) {
      toast.error(
        "Failed to check if claim is batched: " +
          (error.response?.data?.message || error.message),
        { duration: 4000 },
      );
      return false;
    }
  };

  const handleAttachClaim = async (claim) => {
    if (!isBatchSaved) {
      toast.error("Please save the batch header before attaching claims.", {
        duration: 4000,
      });
      return;
    }

    if (claim.claim_status && claim.claim_status !== "NULL") {
      toast.error(`Only claims with NULL status can be attached.`, {
        duration: 4000,
      });
      return;
    }

    setIsProcessing(true);
    setIsProcessingText("Please wait while attaching...");

    // Check if claim is already batched
    // NOTES this is subject for removal if the request encouter slow
    const isAlreadyBatched = await checkIsAlreadyBatched(claim.transaction_no);
    if (isAlreadyBatched.is_batched) {
      toast.error(
        `Claim ${claim.loa_no} is already batched by: ${isAlreadyBatched.user[0].first_name} ${isAlreadyBatched.user[0].last_name}, on batch no: ${isAlreadyBatched.batch_no}`,
        {
          duration: 4000,
        },
      );
      setIsProcessing(false);
      setIsProcessingText("Please wait...");
      return;
    }

    if (claim.is_batched) {
      toast.error(`Claim ${claim.loa_no} is already batched.`, {
        duration: 4000,
      });
      setIsProcessing(false);
      setIsProcessingText("Please wait...");
      return;
    }

    if (!attachedClaims.some((c) => c.id === claim.id)) {
      try {
        const response = await axios.post(
          `${BASE_URL}/claims/attach-claim`,
          {
            batch_no: formData.batch_no,
            transaction_no: claim.transaction_no,
            loa_no: claim.loa_no,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token",
              )}`,
            },
          },
        );

        setAttachedClaims((prev) => [
          ...prev,
          {
            ...claim,
            attachedFiles: [],
            claim_status: claim.stale ? "For RTP" : claim.claim_status,
            amount: Number(claim.amount) || 0,
            claim_amount:
              claim.claim_amount === null ||
              claim.claim_amount === undefined ||
              claim.claim_amount === ""
                ? null
                : Number(claim.claim_amount),
            rtp_tag: Boolean(claim.stale),
            rtp_code: claim.stale ? "1" : "",
            rtp_remarks: claim.stale
              ? "Stale claim (auto-detected during batching)"
              : "",
            rtp_cancel_status: null,
            rtp_cancel_request_id: null,
          },
        ]);
        toast.success(`Claim ${claim.loa_no} attached successfully.`, {
          duration: 3000,
        });

        // Refresh claims to update is_batched status
        fetchClaims();
        setIsProcessing(false);
        setIsProcessingText("Please wait...");
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to attach claim.",
          { duration: 4000 },
        );
        setIsProcessing(false);
        setIsProcessingText("Please wait...");
      }
    } else {
      toast.error(`Claim ${claim.loa_no} is already attached to this batch.`, {
        duration: 3000,
      });
      setIsProcessing(false);
      setIsProcessingText("Please wait...");
    }
  };

  const fetchBatchRtpDetails = async (transactionNo) => {
    const response = await axios.get(`${BASE_URL}/claims/batch-rtp/details`, {
      params: { trans_no: transactionNo },
      headers: {
        Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
      },
    });
    return response.data || {};
  };

  const handleCloseRtpActionModal = () => {
    setIsRtpActionModalOpen(false);
    setRtpModalMode("mark");
    setRtpTargetClaim(null);
    setRtpReasonCode("");
    setRtpCancelReason("");
    setRtpModalRemarks("");
    setRtpModalAttachments([]);
    setRtpModalUploadQueue([]);
    setIsSubmittingRtpAction(false);
  };

  const handleOpenRtpMarkModal = async (claim) => {
    if (!claim?.transaction_no) {
      toast.error("Missing transaction number.", { duration: 4000 });
      return;
    }

    setIsSubmittingRtpAction(true);
    try {
      const details = await fetchBatchRtpDetails(claim.transaction_no);
      const latestRtp = details?.rtp || null;
      setRtpModalMode("mark");
      setRtpTargetClaim(claim);
      setRtpReasonCode(
        claim.rtp_code ||
          latestRtp?.rtp_code ||
          (claim.stale ? "1" : ""),
      );
      setRtpModalRemarks(
        claim.rtp_remarks ||
          latestRtp?.remarks ||
          (claim.stale
            ? "Stale claim (auto-detected during batching)"
            : ""),
      );
      setRtpCancelReason("");
      setRtpModalAttachments(details?.attachments || []);
      setRtpModalUploadQueue([]);
      setIsRtpActionModalOpen(true);
    } catch (error) {
      toast.error(
        resolveApiErrorMessage(error, "Failed to load RTP details."),
        { duration: 4000 },
      );
    } finally {
      setIsSubmittingRtpAction(false);
    }
  };

  const handleOpenRtpCancelModal = async (claim) => {
    if (!claim?.transaction_no) {
      toast.error("Missing transaction number.", { duration: 4000 });
      return;
    }
    if (String(claim.rtp_cancel_status || "").toUpperCase() === "PENDING") {
      toast.error(
        "A cancellation approval request is already pending for this claim.",
        { duration: 4000 },
      );
      return;
    }

    setIsSubmittingRtpAction(true);
    try {
      const details = await fetchBatchRtpDetails(claim.transaction_no);
      setRtpModalMode("cancel_request");
      setRtpTargetClaim(claim);
      setRtpReasonCode(claim.rtp_code || details?.rtp?.rtp_code || "");
      setRtpModalRemarks("");
      setRtpCancelReason("");
      setRtpModalAttachments(details?.attachments || []);
      setRtpModalUploadQueue([]);
      setIsRtpActionModalOpen(true);
    } catch (error) {
      toast.error(
        resolveApiErrorMessage(error, "Failed to load RTP details."),
        { duration: 4000 },
      );
    } finally {
      setIsSubmittingRtpAction(false);
    }
  };

  const handleChooseRtpModalFile = () => {
    rtpModalFileInputRef.current?.click();
  };

  const handleUploadRtpModalAttachment = async (fileObj, transactionNo) => {
    setRtpModalUploadQueue((prev) =>
      prev.map((item) =>
        item.file === fileObj.file ? { ...item, isUploading: true } : item,
      ),
    );

    try {
      const uploadForm = new FormData();
      uploadForm.append("file", fileObj.file);
      uploadForm.append("trans_no", transactionNo);

      const response = await axios.post(
        `${BASE_URL}/claims/batch-rtp/attach-files`,
        uploadForm,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            setRtpModalUploadQueue((prev) =>
              prev.map((item) =>
                item.file === fileObj.file ? { ...item, progress } : item,
              ),
            );
          },
        },
      );

      const uploadedFile = response.data?.files?.[0];
      if (uploadedFile) {
        setRtpModalAttachments((prev) => [
          {
            upload_id: uploadedFile.upload_id,
            original_file_name: uploadedFile.original_file_name,
            signed_url: uploadedFile.signed_url,
            url_expires_at: uploadedFile.expires_at,
          },
          ...prev,
        ]);
      }
      setRtpModalUploadQueue((prev) =>
        prev.filter((item) => item.file !== fileObj.file),
      );
    } catch (error) {
      const errorMessage = resolveApiErrorMessage(error, "Failed to upload file.");
      setRtpModalUploadQueue((prev) =>
        prev.map((item) =>
          item.file === fileObj.file
            ? { ...item, isUploading: false, error: errorMessage }
            : item,
        ),
      );
      toast.error(errorMessage, { duration: 4000 });
    }
  };

  const handleRtpModalFileChange = (selectedFiles) => {
    if (!rtpTargetClaim?.transaction_no) {
      toast.error("Missing transaction number for RTP upload.", {
        duration: 4000,
      });
      return;
    }

    const validFiles = Array.from(selectedFiles).filter((file) => {
      const extension = file.name.split(".").pop().toLowerCase();
      const isAllowedType = allowedFileTypes.includes(extension);
      const isWithinSizeLimit = file.size <= maxFileSizeMB * 1024 * 1024;

      if (!isAllowedType) {
        toast.error(`File ${file.name} is not supported.`, { duration: 4000 });
        return false;
      }
      if (!isWithinSizeLimit) {
        toast.error(`File ${file.name} exceeds ${maxFileSizeMB}MB size limit.`, {
          duration: 4000,
        });
        return false;
      }
      return true;
    });

    const existingCount = rtpModalAttachments.length + rtpModalUploadQueue.length;
    if (existingCount + validFiles.length > maxRtpUploadLimit) {
      toast.error(
        `You can only upload up to ${maxRtpUploadLimit} RTP attachment files.`,
        { duration: 4000 },
      );
      return;
    }

    const queueItems = validFiles.map((file) => ({
      file,
      progress: 0,
      isUploading: false,
      error: null,
    }));
    setRtpModalUploadQueue((prev) => [...prev, ...queueItems]);
    queueItems.forEach((queueItem) =>
      handleUploadRtpModalAttachment(queueItem, rtpTargetClaim.transaction_no),
    );
  };

  const handleRemoveRtpModalAttachment = async (fileObj) => {
    if (!fileObj?.upload_id) {
      return;
    }

    try {
      await axios.delete(
        `${BASE_URL}/claims/batch-rtp/attachments/${fileObj.upload_id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          },
        },
      );

      setRtpModalAttachments((prev) =>
        prev.filter((item) => item.upload_id !== fileObj.upload_id),
      );
      toast.success("Attachment removed.", { duration: 3000 });
    } catch (error) {
      toast.error(
        resolveApiErrorMessage(error, "Failed to remove RTP attachment."),
        { duration: 4000 },
      );
    }
  };

  const handleConfirmRtpAction = async () => {
    if (!rtpTargetClaim?.transaction_no) return;

    if (rtpModalMode === "mark") {
      if (!rtpReasonCode) {
        toast.error("Please select RTP reason.", { duration: 4000 });
        return;
      }

      setAttachedClaims((prev) =>
        prev.map((claim) =>
          claim.transaction_no === rtpTargetClaim.transaction_no
            ? {
                ...claim,
                claim_status: "For RTP",
                rtp_tag: true,
                rtp_code: rtpReasonCode,
                rtp_remarks: rtpModalRemarks,
                rtp_cancel_status: null,
                rtp_cancel_request_id: null,
              }
            : claim,
        ),
      );
      toast.success("Claim marked as For RTP.", { duration: 3000 });
      handleCloseRtpActionModal();
      return;
    }

    if (!rtpCancelReason.trim()) {
      toast.error("Please provide reason for cancellation request.", {
        duration: 4000,
      });
      return;
    }

    setIsSubmittingRtpAction(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/claims/batch-rtp/cancel-request`,
        {
          batch_no: formData.batch_no,
          trans_no: rtpTargetClaim.transaction_no,
          loa_no: rtpTargetClaim.loa_no,
          reason: rtpCancelReason,
          remarks: rtpModalRemarks,
          attachment_upload_ids: rtpModalAttachments
            .map((item) => item.upload_id)
            .filter(Boolean),
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          },
        },
      );

      setAttachedClaims((prev) =>
        prev.map((claim) =>
          claim.transaction_no === rtpTargetClaim.transaction_no
            ? {
                ...claim,
                claim_status: "For RTP",
                rtp_tag: true,
                rtp_cancel_status: "PENDING",
                rtp_cancel_request_id:
                  response.data?.data?.approval_id || claim.rtp_cancel_request_id,
              }
            : claim,
        ),
      );
      toast.success("Cancellation request submitted for approval.", {
        duration: 3000,
      });
      handleCloseRtpActionModal();
    } catch (error) {
      toast.error(
        resolveApiErrorMessage(error, "Failed to submit cancellation request."),
        { duration: 4000 },
      );
    } finally {
      setIsSubmittingRtpAction(false);
    }
  };

  const handleOpenAttachFilesModal = (claim) => {
    setSelectedClaimForFiles(claim);
    setClaimFiles(claim.attachedFiles || []);
    setIsAttachFilesModalOpen(true);
  };

  // const handleRemoveClaim = async (claim) => {
  //   const hasAttachedFiles =
  //     claim.attachedFiles && claim.attachedFiles.length > 0;
  //   const result = await Swal.fire({
  //     title: "Remove Claim?",
  //     text: `Are you sure you want to remove attached claim ${claim.loa_no}? ${
  //       hasAttachedFiles
  //         ? "All uploaded files associated with this claim will also be deleted."
  //         : ""
  //     }`,
  //     icon: "warning",
  //     showCancelButton: true,
  //     confirmButtonColor: "#4f46e5",
  //     cancelButtonColor: "#ef4444",
  //     confirmButtonText: "Yes, remove it",
  //     cancelButtonText: "Cancel",
  //   });

  //   if (result.isConfirmed) {
  //     setIsProcessing(true)
  //     setIsProcessingText('Deleting attached files...')
  //     try {
  //       if (hasAttachedFiles) {
  //         const deletePromises = claim.attachedFiles
  //           .filter((fileObj) => fileObj.uploadId)
  //           .map((fileObj) =>
  //             axios.delete(
  //               `${BASE_URL}/claims/attach-files/${fileObj.uploadId}`,
  //               {
  //                 headers: {
  //                   Authorization: `Bearer ${localStorage.getItem(
  //                     "authentication/token"
  //                   )}`,
  //                 },
  //               }
  //             )
  //           );
  //         await Promise.all(deletePromises);
  //       }

  //       setAttachedClaims((prev) => prev.filter((c) => c.id !== claim.id));
  //       toast.success(`Claim ${claim.loa_no} removed successfully.`, {
  //         duration: 3000,
  //       });
  //       setIsProcessing(false)
  //       setIsProcessingText('Saving Batch...')
  //     } catch (error) {
  //       setIsProcessing(false)
  //       setIsProcessingText('Saving Batch...')
  //       toast.error(
  //         error.response?.data?.message ||
  //           "Failed to remove claim or its files.",
  //         { duration: 4000 }
  //       );
  //     }
  //   }
  // };

  const handleRemoveClaim = async (claim) => {
    if (!isBatchSaved) {
      toast.error("Please save the batch header before removing claims.", {
        duration: 4000,
      });
      return;
    }

    const hasAttachedFiles =
      claim.attachedFiles && claim.attachedFiles.length > 0;
    playConfirmSound();
    const primaryConfirm = await Swal.fire({
      title: "Are you sure?",
      text: `You are about to remove attached claim ${claim.loa_no}. ${
        hasAttachedFiles
          ? "All uploaded files associated with this claim will also be deleted."
          : ""
      }`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, continue",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      customClass: {
        popup: "rounded-2xl",
        actions: "gap-3",
        confirmButton: "rounded-lg px-5 py-2.5 font-semibold",
        cancelButton: "rounded-lg px-5 py-2.5 font-semibold",
      },
      didOpen: () => {
        playConfirmSound();
      },
    });

    if (primaryConfirm.isConfirmed) {
      playConfirmSound();
      const acknowledgmentHtml = `
        <div style="text-align:left; font-size:14px; line-height:1.5;">
          <p><strong>⚠️ Important Warning: Claims Management</strong></p>
          <p>Searching for claims is strictly for special use cases only. Please be aware that all LoAs/Claims originate from the Receiving Module. Once a claim has been attached to a Batch, removing it triggers the following protocols:</p>
          <ul style="padding-left:18px; margin:8px 0;">
            <li><strong>Error Accountability:</strong> Any removal of an attached claim is flagged as an operational error. This reflects on the performance metrics of either the Receiver or the Batcher, depending on where the discrepancy occurred.</li>
            <li><strong>Performance Tracking:</strong> These instances are recorded in individual production reports and are subject to formal investigation.</li>
            <li><strong>System Integrity:</strong> Re-adding or re-attaching claims should not be a routine habit. Use the \"Search\" function only when absolutely necessary for verified exceptions.</li>
          </ul>
          <p><strong>Reminders:</strong> Think before you \"Remove.\" Accuracy in the Batching Module is vital to your production quality and performance evaluation.</p>
        </div>
      `;
      const acknowledgment = await Swal.fire({
        title: "Acknowledgment",
        html: acknowledgmentHtml,
        icon: "info",
        input: "checkbox",
        inputValue: 0,
        inputPlaceholder:
          "I acknowledge the warning above and accept responsibility for removing this claim.",
        showCancelButton: true,
        confirmButtonText: "I Accept Responsibility",
        cancelButtonText: "Go Back",
        confirmButtonColor: "#2563eb",
        cancelButtonColor: "#6b7280",
        customClass: {
          popup: "rounded-2xl",
          actions: "gap-3",
          confirmButton: "rounded-lg px-5 py-2.5 font-semibold",
          cancelButton: "rounded-lg px-5 py-2.5 font-semibold",
        },
        inputValidator: (value) => {
          if (value) return undefined;
          pulseAck();
          return `Please acknowledge before proceeding.<div style="font-size:12px;color:#6b7280;margin-top:4px;">You need to click the checkbox on "I acknowledge the warning..." message before proceeding.</div>`;
        },
        didOpen: () => {
          playConfirmSound();
          applyAckStyles();
        },
      });

      if (!acknowledgment.isConfirmed) {
        return;
      }

      setIsProcessing(true);
      setIsProcessingText("Deleting attached files...");
      try {
        // Delete attached files if they exist
        if (hasAttachedFiles) {
          const deletePromises = claim.attachedFiles
            .filter((fileObj) => fileObj.uploadId)
            .map((fileObj) =>
              axios.delete(
                `${BASE_URL}/claims/attach-files/${fileObj.uploadId}`,
                {
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem(
                      "authentication/token",
                    )}`,
                  },
                },
              ),
            );
          await Promise.all(deletePromises);
        }

        // Delete from v2_claims_is_already_batched
        await axios.delete(
          `${BASE_URL}/claims/attach-claim/${claim.transaction_no}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token",
              )}`,
            },
            data: {
              batch_no: formData.batch_no,
            },
          },
        );

        // Remove the claim from the state
        setAttachedClaims((prev) => prev.filter((c) => c.id !== claim.id));
        toast.success(`Claim ${claim.loa_no} removed successfully.`, {
          duration: 3000,
        });

        // Refresh claims to update is_batched status
        fetchClaims();

        // Trigger the Saving Only
        // handleSubmit_ForSavingOnly()

        setIsProcessing(false);
        setIsProcessingText("Saving Batch...");
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            "Failed to remove claim or its files.",
          { duration: 4000 },
        );
        setIsProcessing(false);
        setIsProcessingText("Saving Batch...");
      }
    }
  };

  // Handle save batch (update)
  const handleSaveBatch = async () => {
    const requiredFields = [
      { key: "received_date", label: "Date Received" },
      { key: "provider_code", label: "Provider Code" },
      { key: "provider_reference_no", label: "Hospital Reference No" },
      { key: "soa_amount", label: "SOA Amount" },
      { key: "accepted_by", label: "Accepted By" },
    ];
    for (const field of requiredFields) {
      const value = formData[field.key];
      if (value === undefined || value === null || String(value).trim() === "") {
        toast.error(`${field.label} is required.`, { duration: 4000 });
        return;
      }
    }

    setIsProcessing(true);
    try {
      const payload = {
        provider_code: formData.provider_code,
        received_date: formData.received_date,
        due_date: formData.due_date,
        provider_reference_no: formData.provider_reference_no,
        soa_amount: Number(formData.soa_amount) || 0,
        batch_type: formData.batch_type,
        batch_status: "Draft",
        act_claim_count: formData.actual_claim_count,
        act_claim_amount: formData.actual_claim_amount,
        created_by: formData.created_by,
        accepted_by: formData.accepted_by,
        physician_code: formData.physician_code,
        remarks: formData.remarks,
        soa_fileupload: formData.soa_fileupload,
        batch_no: formData.batch_no,
        type: "save_header",
      };
      const response = await axios.post(
        `${BASE_URL}/claims/batch-header/save`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token",
            )}`,
          },
        },
      );
      setIsBatchSaved(true);
      Swal.fire({
        title: "Success!",
        text: "Batch header updated successfully.",
        icon: "success",
        confirmButtonColor: "#4f46e5",
      });
      setFormData((prev) => ({
        ...prev,
        est_claim_count: response.data.est_claim_count || prev.est_claim_count,
        est_claim_amount:
          response.data.est_claim_amount || prev.est_claim_amount,
      }));
      await endEditSession();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update batch.", {
        duration: 4000,
      });
      hasUnsavedChanges.current = true;
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle submit batch
  const handleSubmit = async () => {
    if (attachedClaims.length === 0) {
      toast.error("At least one claim must be attached.", { duration: 4000 });
      return;
    }

    setIsConfirmModalOpen(true);
  };

  // Handle save batch ForSavingOnly
  const handleSaveBatch_ForSavingOnly = async () => {
    const requiredFields = [
      { key: "received_date", label: "Date Received" },
      { key: "provider_code", label: "Provider Code" },
      { key: "provider_reference_no", label: "Hospital Reference No" },
      { key: "soa_amount", label: "SOA Amount" },
      { key: "accepted_by", label: "Accepted By" },
    ];
    for (const field of requiredFields) {
      const value = formData[field.key];
      if (value === undefined || value === null || String(value).trim() === "") {
        toast.error(`${field.label} is required.`, { duration: 4000 });
        return;
      }
    }

    setIsProcessing(true);
    try {
      const payload = {
        provider_code: formData.provider_code,
        received_date: formData.received_date,
        due_date: formData.due_date,
        provider_reference_no: formData.provider_reference_no,
        soa_amount: Number(formData.soa_amount) || 0,
        batch_type: formData.batch_type,
        batch_status: "Open",
        act_claim_count: formData.actual_claim_count,
        act_claim_amount: formData.actual_claim_amount,
        created_by: formData.created_by,
        accepted_by: formData.accepted_by,
        physician_code: formData.physician_code,
        remarks: formData.remarks,
        soa_fileupload: formData.soa_fileupload,
        batch_no: formData.batch_no,
        type: "save_header",
      };
      const response = await axios.post(
        `${BASE_URL}/claims/batch-header/save`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token",
            )}`,
          },
        },
      );
      setIsBatchSaved(true);
      Swal.fire({
        title: "Success!",
        text: "Batch header saved successfully.",
        icon: "success",
        confirmButtonColor: "#4f46e5",
      });
      setFormData((prev) => ({
        ...prev,
        est_claim_count: response.data.est_claim_count,
        est_claim_amount: response.data.est_claim_amount,
      }));
      hasUnsavedChanges.current = false;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save batch.", {
        duration: 4000,
      });
      hasUnsavedChanges.current = true;
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle submit batch ForSavingOnly
  const handleSubmit_ForSavingOnly = async () => {
    if (!isBatchSaved) {
      // Save batch header first
      await handleSaveBatch_ForSavingOnly();
      if (!isBatchSaved) {
        toast.error("Failed to save batch header. Please try again.", {
          duration: 4000,
        });
        return;
      }
    }

    // if (attachedClaims.length === 0) {
    //   toast.error("At least one claim must be attached.", { duration: 4000 });
    //   return;
    // }

    // setIsConfirmModalOpen(true); // Show confirmation modal

    handleConfirmSubmit_ForSavingOnly();
  };

  // Handle confirm submit ForSavingOnly
  // const handleConfirmSubmit_ForSavingOnly = async () => {
  //   setIsProcessing(true);
  //   setIsTableLoading(true);
  //   try {
  //     const payload = {
  //       is_save_only: true,
  //       batch_no: formData.batch_no,
  //       provider_code: formData.provider_code,
  //       received_date: formData.received_date,
  //       due_date: formData.due_date,
  //       provider_reference_no: formData.provider_reference_no,
  //       batch_type: formData.batch_type,
  //       est_claim_count: formData.est_claim_count,
  //       est_claim_amount: formData.est_claim_amount,
  //       act_claim_count: formData.actual_claim_count,
  //       act_claim_amount: formData.actual_claim_amount,
  //       created_by: formData.created_by,
  //       accepted_by: formData.accepted_by,
  //       physician_code: formData.physician_code,
  //       remarks: formData.remarks,
  //       soa_fileupload: formData.soa_fileupload,
  //       claims: attachedClaims.map((claim) => ({
  //         transaction_no: claim.transaction_no,
  //         claim_status: claim.claim_status || null,
  //         claim_remarks: claim.claim_remarks || "",
  //       })),
  //     };

  //     const response = await axios.post(
  //       `${BASE_URL}/claims/batch-header/submit-save-only`,
  //       payload,
  //       {
  //         headers: {
  //           Authorization: `Bearer ${localStorage.getItem(
  //             "authentication/token"
  //           )}`,
  //         },
  //       }
  //     );

  //     await endEditSession();

  //     // Show success and prompt for next action
  //     const result = await Swal.fire({
  //       title: "Batch Saved Successfully!",
  //       // text: "What would you like to do next?",
  //       icon: "success",
  //       // showCancelButton: true,
  //       confirmButtonColor: "#4f46e5",
  //       cancelButtonColor: "#6b7280",
  //       confirmButtonText: "Go to Claims Batch List",
  //       cancelButtonText: "Go to Claims Batch List",
  //       customClass: {
  //         popup: "rounded-xl shadow-lg",
  //         title: "text-lg font-semibold text-gray-900",
  //         content: "text-sm text-gray-600",
  //         confirmButton:
  //           "px-4 py-2 rounded-md text-white bg-indigo-600 hover:bg-indigo-700 transition-all",
  //         cancelButton:
  //           "px-4 py-2 rounded-md text-white bg-gray-500 hover:bg-gray-600 transition-all",
  //       },
  //     });

  //     if (result.isConfirmed) {
  //       hasUnsavedChanges.current = false;
  //       navigate("/claims/batches");
  //       // Create New Batch
  //       // setFormData({
  //       //   batch_no: "",
  //       //   batch_status: "Open",
  //       //   received_date: "",
  //       //   due_date: "",
  //       //   provider_code: "",
  //       //   provider_reference_no: "",
  //       //   accepted_by: "",
  //       //   actual_claim_count: 0,
  //       //   actual_claim_amount: 0,
  //       //   batch_type: "Claims",
  //       //   est_claim_count: 0,
  //       //   est_claim_amount: 0,
  //       //   created_by: "system_user",
  //       //   physician_code: null,
  //       //   remarks: "",
  //       //   soa_fileupload: "[]",
  //       // });
  //       // setFiles([]);
  //       // setAttachedClaims([]);
  //       // setIsBatchSaved(false);
  //       // setIsConfirmModalOpen(false);
  //       // generateBatchNumber();

  //     } else {
  //       hasUnsavedChanges.current = false;
  //       // Navigate to Claims Batch List
  //       navigate("/claims/batches");
  //     }
  //   } catch (error) {
  //     toast.error(error.response?.data?.message || "Failed to submit batch.", {
  //       duration: 4000,
  //     });
  //     hasUnsavedChanges.current = true;
  //   } finally {
  //     setIsProcessing(false);
  //     setIsTableLoading(false);
  //   }
  // };

  const handleConfirmSubmit_ForSavingOnly = async () => {
    setIsProcessing(true);
    setIsTableLoading(true);

    try {
      // Validate required fields
      const requiredFields = {
        due_date: formData.due_date,
        provider_reference_no: formData.provider_reference_no,
        soa_amount: formData.soa_amount,
        accepted_by: formData.accepted_by,
      };

      const missingFields = Object.entries(requiredFields)
        .filter(([_, value]) => value === undefined || value === null || String(value).trim() === "")
        .map(([key]) => key);

      if (missingFields.length > 0) {
        const fieldNames = missingFields
          .map((field) => field.replace("_", " ").toUpperCase())
          .join(", ");
        toast.error(
          `Please fill in the following required fields: ${fieldNames}`,
          {
            duration: 5000,
          },
        );
        return;
      }

      // Prepare payload
      const payload = {
        is_save_only: true,
        batch_no: formData.batch_no || "",
        provider_code: formData.provider_code || "",
        received_date: formData.received_date || "",
        due_date: formData.due_date,
        provider_reference_no: formData.provider_reference_no,
        soa_amount: Number(formData.soa_amount) || 0,
        batch_type: formData.batch_type || "Claims",
        est_claim_count: formData.est_claim_count || 0,
        est_claim_amount: formData.est_claim_amount || 0,
        act_claim_count: formData.actual_claim_count || 0,
        act_claim_amount: formData.actual_claim_amount || 0,
        created_by: formData.created_by || "system_user",
        accepted_by: formData.accepted_by,
        physician_code: formData.physician_code || null,
        remarks: formData.remarks || "",
        soa_fileupload: formData.soa_fileupload || "[]",
        claims: attachedClaims.map((claim) => ({
          transaction_no: claim.transaction_no || "",
          claim_status: claim.claim_status || null,
          claim_remarks: claim.claim_remarks || "",
          rtp_tag: claim.rtp_tag ? 1 : 0,
          rtp_code: claim.rtp_code || "",
          rtp_remarks: claim.rtp_remarks || "",
        })),
      };

      // Send request
      const response = await axios.post(
        `${BASE_URL}/claims/batch-header/submit-save-only`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token",
            )}`,
          },
        },
      );

      // End edit session
      await endEditSession();

      // Show success message
      await Swal.fire({
        title: "Batch Saved Successfully!",
        icon: "success",
        confirmButtonColor: "#4f46e5",
        confirmButtonText: "Go to Claims Batch List",
        customClass: {
          popup: "rounded-xl shadow-lg",
          title: "text-lg font-semibold text-gray-900",
          confirmButton:
            "px-4 py-2 rounded-md text-white bg-indigo-600 hover:bg-indigo-700 transition-all",
        },
      });

      // Navigate to batch list
      hasUnsavedChanges.current = false;
      navigate("/claims/batches");
    } catch (error) {
      // Handle specific error cases
      if (error.response?.status === 422) {
        const errors = error.response.data.message;
        let errorMessages = "";
        if (errors && typeof errors === "object" && !Array.isArray(errors)) {
          errorMessages = Object.entries(errors)
            .map(([field, messages]) => {
              let text = "";
              if (Array.isArray(messages)) {
                text = messages.join(", ");
              } else if (typeof messages === "string") {
                text = messages;
              } else if (messages && typeof messages === "object") {
                text = Object.values(messages).flat().join(", ");
              } else {
                text = String(messages);
              }
              return `${field.replace("_", " ")}: ${text}`;
            })
            .join("; ");
        } else if (Array.isArray(errors)) {
          errorMessages = errors.join(", ");
        } else if (typeof errors === "string") {
          errorMessages = errors;
        } else {
          errorMessages = resolveApiErrorMessage(error, "Validation failed.");
        }
        toast.error(`Validation failed: ${errorMessages}`, {
          duration: 6000,
        });
      } else {
        toast.error(
          error.response?.data?.message || "Failed to submit batch.",
          {
            duration: 4000,
          },
        );
      }
      hasUnsavedChanges.current = true;
    } finally {
      setIsProcessing(false);
      setIsTableLoading(false);
    }
  };

  // Handle confirm submit
  const handleConfirmSubmit = async () => {
    setIsProcessing(true);
    setIsTableLoading(true);
    try {
      const payload = {
        batch_no: formData.batch_no,
        provider_code: formData.provider_code,
        received_date: formData.received_date,
        due_date: formData.due_date,
        provider_reference_no: formData.provider_reference_no,
        soa_amount: Number(formData.soa_amount) || 0,
        batch_type: formData.batch_type,
        est_claim_count: formData.est_claim_count,
        est_claim_amount: formData.est_claim_amount,
        act_claim_count: formData.actual_claim_count,
        act_claim_amount: formData.actual_claim_amount,
        created_by: formData.created_by,
        accepted_by: formData.accepted_by,
        physician_code: formData.physician_code,
        remarks: formData.remarks,
        soa_fileupload: formData.soa_fileupload,
        claims: attachedClaims.map((claim) => ({
          transaction_no: claim.transaction_no,
          claim_status: claim.claim_status || null,
          claim_remarks: claim.claim_remarks || "",
          rtp_tag: claim.rtp_tag ? 1 : 0,
          rtp_code: claim.rtp_code || "",
          rtp_remarks: claim.rtp_remarks || "",
        })),
      };

      await axios.post(`${BASE_URL}/claims/batch-header/submit`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem(
            "authentication/token",
          )}`,
        },
      });

      await endEditSession();

      const result = await Swal.fire({
        title: "Batch Updated Successfully!",
        text: "What would you like to do next?",
        icon: "success",
        showCancelButton: true,
        confirmButtonColor: "#4f46e5",
        cancelButtonColor: "#6b7280",
        confirmButtonText: "Create New Batch",
        cancelButtonText: "Go to Claims Batch List",
      });

      if (result.isConfirmed) {
        navigate("/claims/batches/add");
      } else {
        navigate("/claims/batches");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to submit batch.", {
        duration: 4000,
      });
    } finally {
      setIsProcessing(false);
      setIsTableLoading(false);
    }
  };

  // Utility to convert data URL to Blob (for scanned images)
  const dataURLtoBlob = (dataURL) => {
    const arr = dataURL.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const handleScanFiles = (claim) => {
    if (!claim) {
      toast.error("No claim selected for scanning.", { duration: 4000 });
      return;
    }
    if (!window.scanner || typeof window.scanner.scan !== "function") {
      const token = localStorage.getItem("authentication/token") || "";
      const soaNo = claim.soa || formData.provider_reference_no || "";
      const scanUrl = buildCustomScanUrl({
        type: "loa",
        loa_no: claim.loa_no || "",
        transaction_no: claim.transaction_no || "",
        batch_no: formData.batch_no || "",
        token,
        soa_no: soaNo,
      });
      setScanWaitingData({
        type: "loa",
        loa_no: claim.loa_no || "-",
        transaction_no: claim.transaction_no || "-",
        batch_no: formData.batch_no || "-",
      });
      setIsWaitingForScan(true);
      toast.error(
        "Scanner is not available. Please open the Custom Scan App or load the scanner client.",
        { duration: 6000 },
      );
      window.location.href = scanUrl;
      return;
    }

    setSelectedClaimForFiles(claim);
    setClaimFiles(claim.attachedFiles || []);
    setIsProcessing(true);
    setIsProcessingText("Scanning documents...");
    setScanning(true);

    window.scanner.scan(
      (success, response) => {
        console.log("Scanner.js response:", { success, response }); // Log response for debugging
        if (!success || !response || !response.output) {
          setIsProcessing(false);
          setIsProcessingText("Saving Batch...");
          setScanning(false);
          if (!success) {
            toast.error(
              "Scanning failed: " + (response?.message || "Unknown error"),
              { duration: 4000 },
            );
            if (response?.message?.includes("Asprise Scan Client")) {
              toast.error(
                "Please install the Asprise Scan Client: http://asprise.com/scan/client/download.php",
                { duration: 6000 },
              );
            }
          } else {
            toast.error("No images scanned or invalid response from scanner.", {
              duration: 4000,
            });
          }
          return;
        }

        const scannedImages = window.scanner.getScannedImages(
          response,
          true,
          false,
        );
        console.log("Scanned images:", scannedImages); // Log images for debugging
        if (scannedImages.length === 0) {
          setIsProcessing(false);
          setIsProcessingText("Saving Batch...");
          setScanning(false);
          toast.error("No images were scanned.", { duration: 4000 });
          return;
        }

        const newFiles = scannedImages.map((img, index) => {
          const blob = dataURLtoBlob(img.src);
          return {
            file: new File(
              [blob],
              `scanned_${claim.transaction_no}_${index + 1}.${
                img.src.includes("jpeg") ? "jpg" : "png"
              }`,
              {
                type: img.src.includes("jpeg") ? "image/jpeg" : "image/png",
              },
            ),
            progress: 0,
            isUploading: false,
            uploadId: null,
            signed_url: null,
            error: null,
          };
        });

        setClaimFiles((prevFiles) => [...prevFiles, ...newFiles]);
        newFiles.forEach((fileObj) => handleClaimFileUpload(fileObj));

        setIsProcessing(false);
        setIsProcessingText("Saving Batch...");
        setScanning(false);
      },
      {
        source: "scanner",
        format: "jpg",
        output_settings: [
          {
            type: "return-base64",
            format: "jpg",
          },
        ],
      },
    );
  };

  const handleScanSoaFiles = () => {
    const token = localStorage.getItem("authentication/token") || "";
    const dateReceived = formatScanDate(formData.received_date);
    const soaNo = formData.provider_reference_no || "";
    const batchNo = formData.batch_no || "";

    if (!batchNo) {
      toast.error("Missing batch number. Please save the batch first.", {
        duration: 4000,
      });
      return;
    }

    if (!window.scanner || typeof window.scanner.scan !== "function") {
      const scanUrl = buildCustomScanUrl({
        type: "soa",
        batch_no: batchNo,
        token,
        soa_no: soaNo,
      });
      setScanWaitingData({
        type: "soa",
        soa_no: soaNo || "-",
        batch_no: batchNo || "-",
        date_received: dateReceived || "-",
      });
      setIsWaitingForScan(true);
      toast.error(
        "Scanner is not available. Please open the Custom Scan App or load the scanner client.",
        { duration: 6000 },
      );
      window.location.href = scanUrl;
      return;
    }

    setIsProcessing(true);
    setIsProcessingText("Scanning documents...");
    setScanning(true);

    window.scanner.scan(
      (success, response) => {
        if (!success || !response || !response.output) {
          setIsProcessing(false);
          setIsProcessingText("Saving Batch...");
          setScanning(false);
          if (!success) {
            toast.error(
              "Scanning failed: " + (response?.message || "Unknown error"),
              { duration: 4000 },
            );
            if (response?.message?.includes("Asprise Scan Client")) {
              toast.error(
                "Please install the Asprise Scan Client: http://asprise.com/scan/client/download.php",
                { duration: 6000 },
              );
            }
          } else {
            toast.error("No images scanned or invalid response from scanner.", {
              duration: 4000,
            });
          }
          return;
        }

        const scannedImages = window.scanner.getScannedImages(
          response,
          true,
          false,
        );
        if (scannedImages.length === 0) {
          setIsProcessing(false);
          setIsProcessingText("Saving Batch...");
          setScanning(false);
          toast.error("No images were scanned.", { duration: 4000 });
          return;
        }

        const newFiles = scannedImages.map((img, index) => {
          const blob = dataURLtoBlob(img.src);
          return {
            file: new File(
              [blob],
              `soa_${batchNo}_${index + 1}.${img.src.includes("jpeg") ? "jpg" : "png"}`,
              {
                type: img.src.includes("jpeg") ? "image/jpeg" : "image/png",
              },
            ),
            progress: 0,
            isUploading: false,
            uploadId: null,
            signed_url: null,
            error: null,
          };
        });

        setFiles((prevFiles) => [...prevFiles, ...newFiles]);
        newFiles.forEach((fileObj) => handleFileUpload(fileObj));

        setIsProcessing(false);
        setIsProcessingText("Saving Batch...");
        setScanning(false);
      },
      {
        source: "scanner",
        format: "jpg",
        output_settings: [
          {
            type: "return-base64",
            format: "jpg",
          },
        ],
      },
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
      <Tooltip id="provider-tooltip" />
      <Tooltip id="search-claims-tooltip" />
      <Tooltip id="filter-claims-tooltip" />
      <Tooltip id="view-claim-tooltip" />
      <Tooltip id="attach-claim-tooltip" />
      <Tooltip id="attach-files-tooltip" />
      <Tooltip id="scan-files-tooltip" />
      <Tooltip id="remove-claim-tooltip" />
      <Tooltip id="view-file-tooltip" />
      <Tooltip id="remove-file-tooltip" />
      <Tooltip id="save-batch-tooltip" />
      <Tooltip id="submit-batch-tooltip" />
      <Tooltip id="items-per-page-tooltip" />

      <div className="max-w-1xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 bg-white rounded-2xl shadow-lg">
            <FontAwesomeIcon
              icon={faUpload}
              className="text-indigo-600 text-lg"
            />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Edit Claims Batch
            </h1>
            <p className="text-sm text-gray-500">
              Update an existing claims batch and manage attached claims
            </p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Batch Registration Set-up (Left) */}
          {isStep1Visible && (
            <div className="md:w-1/3 bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-800">
                  Batch Registration Set-up
                </h2>
                <button
                  onClick={() => setIsStep1Visible(false)}
                  className="text-indigo-600 hover:text-indigo-800"
                  aria-label="Hide Batch Registration Set-up"
                >
                  <FontAwesomeIcon icon={faEyeSlash} />
                </button>
              </div>
              <div className="p-6">
                {/* Form Fields */}
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Batch Number
                    </label>
                    <input
                      type="text"
                      value={formData.batch_no}
                      disabled
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-100 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Batch Status
                    </label>
                    <input
                      type="text"
                      value={formData.batch_status}
                      disabled
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-100 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Date Received *
                    </label>
                    <input
                      type="datetime-local"
                      name="received_date"
                      value={formData.received_date}
                      onChange={handleInputChange}
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Due Date
                    </label>
                    <input
                      type="date"
                      value={formData.due_date}
                      disabled
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-100 cursor-not-allowed"
                    />
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Provider *
                    </label>
                    <input
                      type="text"
                      value={selectedProviderName}
                      onChange={handleProviderSearch}
                      placeholder="Search providers..."
                      className={`mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                        !formData.received_date
                          ? "bg-gray-100 cursor-not-allowed"
                          : ""
                      } ${isProviderLoading ? "pr-9" : ""}`}
                      disabled={!formData.received_date}
                      data-tooltip-id="provider-tooltip"
                      data-tooltip-content={
                        !formData.received_date
                          ? "Enter Date Received to search providers"
                          : "Search providers"
                      }
                    />
                    {isProviderLoading && (
                      <span className="absolute right-3 top-9 text-gray-400">
                        <FontAwesomeIcon
                          icon={faSpinner}
                          className="animate-spin"
                        />
                      </span>
                    )}
                    {providers.length > 0 && formData.received_date && (
                      <ul className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg mt-1 max-h-40 overflow-y-auto shadow-lg">
                        {providers.map((provider) => (
                          <li
                            key={provider.id}
                            onClick={() => handleProviderSelect(provider)}
                            className="px-3 py-2 text-sm hover:bg-indigo-100 cursor-pointer"
                          >
                            {provider.provider_name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Hospital Reference No *
                    </label>
                    <input
                      type="text"
                      name="provider_reference_no"
                      value={formData.provider_reference_no}
                      onChange={handleInputChange}
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      SOA Amount *
                    </label>
                    <input
                      type="number"
                      name="soa_amount"
                      min="0"
                      step="0.01"
                      value={formData.soa_amount}
                      readOnly
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-100 cursor-not-allowed"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Accepted By *
                    </label>
                    <input
                      type="text"
                      name="accepted_by"
                      value={formData.accepted_by}
                      onChange={handleInputChange}
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Actual Claim Count
                    </label>
                    <input
                      type="number"
                      value={formData.actual_claim_count}
                      disabled
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-100 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Actual Claim Amount
                    </label>
                    <input
                      type="text"
                      value={phpFormatter.format(formData.actual_claim_amount)}
                      disabled
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-100 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Remarks
                    </label>
                    <textarea
                      name="remarks"
                      value={formData.remarks}
                      onChange={handleInputChange}
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      rows="4"
                      placeholder="Enter any additional remarks..."
                    />
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end mt-6">
                  <button
                    onClick={handleSaveBatch}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
                    disabled={
                      isProcessing ||
                      files.some((f) => f.isUploading) ||
                      !formData.batch_no
                    }
                    data-tooltip-id="save-batch-tooltip"
                    data-tooltip-content="Update batch header"
                  >
                    <FontAwesomeIcon icon={faSave} />
                    Update Batch Header
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Search Claims (Right) */}
          <div
            className={`bg-white rounded-2xl shadow-xl overflow-hidden ${
              isStep1Visible ? "md:w-2/3" : "w-full"
            }`}
          >
            <div className="p-6 flex justify-between items-center border-b border-gray-100">
              <h2 className="text-xl font-semibold text-gray-800">
                Search Claims
              </h2>
              <div className="flex items-center gap-3">
                {!isStep1Visible && (
                  <button
                    onClick={() => setIsStep1Visible(true)}
                    className="text-indigo-600 hover:text-indigo-800"
                    aria-label="Show Batch Registration Set-up"
                  >
                    Show Batch Registration Set-up{" "}
                    <FontAwesomeIcon icon={faEye} />
                  </button>
                )}
                <button
                  onClick={() => setIsSearchClaimsOpen((prev) => !prev)}
                  className="text-indigo-600 hover:text-indigo-800 flex items-center gap-2"
                  aria-label="Toggle Search Claims"
                >
                  {isSearchClaimsOpen ? "Hide" : "Show"} Search Claims
                  <FontAwesomeIcon
                    icon={isSearchClaimsOpen ? faChevronUp : faChevronDown}
                  />
                </button>
              </div>
            </div>
            {isSearchClaimsOpen && (
              <div className="p-6">
                {/* Search Results Table */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    <span>Search Results</span>
                    <br />
                    {/* <span className="text-sm font-normal text-amber-700 bg-amber-50 px-3 py-1 rounded-lg border border-amber-200">
      <strong className="font-bold">Warning:</strong> Please be reminded that searching for claims is for special cases only. Once an attached claim is removed, you may re-add or re-attach it using this section.
    </span> */}
                    <div className="text-sm font-normal text-amber-800 bg-amber-50 p-4 rounded-lg border border-amber-200 shadow-sm leading-relaxed">
                      <div className="flex items-center mb-2 text-amber-900">
                        <strong className="font-bold flex items-center gap-2 text-base">
                          ⚠️ Important Warning: Claims Management
                        </strong>
                      </div>

                      <p className="mb-3">
                        Searching for claims is{" "}
                        <strong>strictly for special use cases only</strong>.
                        Please be aware that all LoAs/Claims originate from the
                        Receiving Module. Once a claim has been attached to a
                        Batch, removing it triggers the following protocols:
                      </p>

                      <ul className="list-disc ml-5 space-y-2 mb-3">
                        <li>
                          <strong className="font-semibold">
                            Error Accountability:
                          </strong>{" "}
                          Any removal of an attached claim is flagged as an
                          operational error. This reflects on the performance
                          metrics of either the <strong>Receiver</strong> or the{" "}
                          <strong>Batcher</strong>.
                        </li>
                        <li>
                          <strong className="font-semibold">
                            Performance Tracking:
                          </strong>{" "}
                          These instances are recorded in individual production
                          reports and are subject to{" "}
                          <strong>formal investigation</strong>.
                        </li>
                        <li>
                          <strong className="font-semibold">
                            System Integrity:
                          </strong>{" "}
                          Re-adding or re-attaching claims should not be a
                          routine habit. Use the "Search" function only when
                          absolutely necessary for verified exceptions.
                        </li>
                      </ul>

                      <p className="italic font-medium border-t border-amber-200 pt-2 mt-2">
                        Reminder: Think before you "Remove." Accuracy in the
                        Batching Module is vital to your production quality and
                        performance evaluation.
                      </p>
                    </div>
                  </h3>
                  <div className="flex flex-col gap-4 mb-4">
                    <div className="relative">
                      <FontAwesomeIcon
                        icon={faSearch}
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="text"
                        placeholder="Search claims..."
                        value={searchTerm}
                        onChange={handleSearch}
                        className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        data-tooltip-id="search-claims-tooltip"
                        data-tooltip-content="Search claims"
                      />
                    </div>
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Date From
                        </label>
                        <input
                          type="date"
                          value={dateFilters.date_from}
                          onChange={(e) =>
                            setDateFilters((prev) => ({
                              ...prev,
                              date_from: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          data-tooltip-id="filter-claims-tooltip"
                          data-tooltip-content="Filter by Date From"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Date To
                        </label>
                        <input
                          type="date"
                          value={dateFilters.date_to}
                          onChange={(e) =>
                            setDateFilters((prev) => ({
                              ...prev,
                              date_to: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          data-tooltip-id="filter-claims-tooltip"
                          data-tooltip-content="Filter by Date To"
                        />
                      </div>
                    </div>
                    <div className="flex gap-4 overflow-x-auto">
                      {columns.map(
                        (col) =>
                          col.key !== "actions" &&
                          col.key !== "amount" &&
                          col.key !== "date" &&
                          col.key !== "stale" &&
                          col.key !== "claim_status" && (
                            <div key={col.key} className="flex-1 min-w-[150px]">
                              <input
                                type="text"
                                placeholder={`Filter by ${col.label}`}
                                value={filters[col.key]}
                                onChange={(e) =>
                                  handleFilterChange(col.key, e.target.value)
                                }
                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                data-tooltip-id="filter-claims-tooltip"
                                data-tooltip-content={`Filter by ${col.label}`}
                              />
                            </div>
                          ),
                      )}
                      <div className="flex-1 min-w-[150px]">
                        <select
                          value={filters.claim_status}
                          onChange={(e) =>
                            handleFilterChange("claim_status", e.target.value)
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          data-tooltip-id="filter-claims-tooltip"
                          data-tooltip-content="Filter by Claim Status"
                        >
                          <option value="">All</option>
                          <option value="NULL">NULL</option>
                          {Object.keys(claimStatusStyles)
                            .filter((status) => status !== "NULL")
                            .map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-x-auto relative">
                    {isSearchTableLoading && (
                      <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                        <FontAwesomeIcon
                          icon={faSpinner}
                          className="text-indigo-600 text-2xl animate-spin"
                        />
                      </div>
                    )}
                    <table className="w-full table-auto">
                      <thead className="bg-gray-50">
                        <tr>
                          {columns.map((column) => (
                            <th
                              key={column.key}
                              className={`p-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider ${
                                column.sortable
                                  ? "cursor-pointer hover:bg-gray-100"
                                  : ""
                              }`}
                              onClick={() =>
                                column.sortable && handleSort(column.key)
                              }
                            >
                              <div className="flex items-center gap-1">
                                {column.label}
                                {column.sortable && (
                                  <div className="flex flex-col text-xs">
                                    <span
                                      className={`${
                                        sortConfig.key === column.key &&
                                        sortConfig.direction === "asc"
                                          ? "text-indigo-600"
                                          : "text-gray-400"
                                      }`}
                                    >
                                      ▲
                                    </span>
                                    <span
                                      className={`${
                                        sortConfig.key === column.key &&
                                        sortConfig.direction === "desc"
                                          ? "text-indigo-600"
                                          : "text-gray-400"
                                      }`}
                                    >
                                      ▼
                                    </span>
                                  </div>
                                )}
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {claims.length === 0 && !isSearchTableLoading ? (
                          <tr>
                            <td
                              colSpan={columns.length}
                              className="p-3 text-center text-gray-500 text-sm"
                            >
                              No claims found.
                            </td>
                          </tr>
                        ) : (
                          claims.map((claim) => (
                            <tr
                              key={claim.id}
                              className="border-b hover:bg-gray-50"
                            >
                              <td className="p-3 text-sm">{claim.loa_no}</td>
                              <td className="p-3 text-sm">
                                {claim.transaction_no}
                              </td>
                              <td className="p-3 text-sm">
                                {phpFormatter.format(claim.amount)}
                              </td>
                              <td className="p-3 text-sm">
                                {claim.claim_amount === null ||
                                claim.claim_amount === undefined ||
                                claim.claim_amount === ""
                                  ? "-"
                                  : phpFormatter.format(claim.claim_amount)}
                              </td>
                              <td className="p-3 text-sm">
                                {claim.patient_name}
                              </td>
                              <td className="p-3 text-sm">{claim.coverage}</td>
                              <td className="p-3 text-sm">{claim.date}</td>
                              <td className="p-3 text-sm">
                                <span
                                  className={`px-2 py-1 rounded-full text-xs ${
                                    claimStatusStyles[claim.claim_status] ||
                                    claimStatusStyles["NULL"]
                                  }`}
                                >
                                  {claim.claim_status || "NULL"}
                                </span>
                              </td>
                              <td className="p-3 text-sm">
                                {claim.stale ? "Yes" : "No"}
                              </td>
                              <td className="p-3">
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleViewClaim(claim)}
                                    className="px-3 py-1 text-xs text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 flex items-center gap-1"
                                    data-tooltip-id="view-claim-tooltip"
                                    data-tooltip-content="View claim details"
                                  >
                                    <FontAwesomeIcon icon={faEye} />
                                    View
                                  </button>
                                  <button
                                    onClick={() => handleAttachClaim(claim)}
                                    className={`px-3 py-1 text-xs text-white bg-green-600 rounded-lg hover:bg-green-700 flex items-center gap-1 ${
                                      claim.claim_status &&
                                      claim.claim_status !== "NULL"
                                        ? "opacity-50 cursor-not-allowed"
                                        : ""
                                    }`}
                                    disabled={
                                      claim.claim_status &&
                                      claim.claim_status !== "NULL"
                                    }
                                    data-tooltip-id="attach-claim-tooltip"
                                    data-tooltip-content={
                                      claim.claim_status &&
                                      claim.claim_status !== "NULL"
                                        ? "Only NULL status claims can be attached"
                                        : "Attach claim to batch"
                                    }
                                  >
                                    <FontAwesomeIcon icon={faPaperclip} />
                                    Attach Claim
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex flex-col md:flex-row md:items-center justify-between p-6 border-t border-gray-100">
                    <div className="mb-3 md:mb-0">
                      <select
                        value={itemsPerPage}
                        onChange={(e) =>
                          handleItemsPerPageChange(Number(e.target.value))
                        }
                        className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        data-tooltip-id="items-per-page-tooltip"
                        data-tooltip-content="Items per page"
                      >
                        {[5, 10, 20, 50, 100].map((val) => (
                          <option key={val} value={val}>
                            {val} per page
                          </option>
                        ))}
                      </select>
                      <span className="ml-2 text-sm text-gray-600">
                        {showingEntries}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                        className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
                      >
                        <FontAwesomeIcon icon={faAngleDoubleLeft} />
                      </button>
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
                      >
                        <FontAwesomeIcon icon={faAngleLeft} />
                      </button>
                      {renderPageNumbers()}
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
                      >
                        <FontAwesomeIcon icon={faAngleRight} />
                      </button>
                      <button
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                        className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
                      >
                        <FontAwesomeIcon icon={faAngleDoubleRight} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* File Upload Area */}
            <div className="px-6 pt-2">
              <div className="mt-6">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Attach Statement of Account (SOA)
                  </h3>
                  <button
                    type="button"
                    onClick={handleScanSoaFiles}
                    disabled={scanning || isWaitingForScan}
                    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                  >
                    <FontAwesomeIcon icon={faCamera} />
                    Scan Files to Attach (SOA)
                  </button>
                </div>
                <div
                  className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 ${
                    isDragging
                      ? "border-indigo-500 bg-indigo-50 scale-105"
                      : "border-gray-300 bg-gray-50"
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <FontAwesomeIcon
                    icon={faUpload}
                    className="text-4xl text-indigo-500 mb-4"
                  />
                  <p className="text-gray-700 text-lg">
                    Drag and drop PDF or image files here or{" "}
                    <button
                      type="button"
                      onClick={handleChooseFile}
                      className="text-indigo-600 font-semibold hover:text-indigo-800 transition-colors"
                    >
                      choose files
                    </button>
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    Supported formats: .pdf, .jpg, .jpeg, .png (Max{" "}
                    {maxFileSizeMB}MB per file, up to {maxUploadLimit} files)
                  </p>
                  <input
                    type="file"
                    ref={fileInputRef}
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e.target.files)}
                    className="hidden"
                  />
                </div>
              </div>

              {files.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Selected (SOA) Files
                  </h3>
                  <ul className="space-y-4">
                    {files.map((fileObj, index) => (
                      <li
                        key={index}
                        className="flex items-center justify-between bg-gray-100 p-4 rounded-lg shadow-sm"
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {fileObj.file.name}
                          </p>
                          {fileObj.isUploading && (
                            <div className="relative pt-2">
                              <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
                                <div
                                  className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${fileObj.progress}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-600 mt-1 block">
                                {fileObj.progress}% Uploading
                              </span>
                            </div>
                          )}
                          {fileObj.error && (
                            <p className="text-xs text-red-600 mt-1">
                              Error: {fileObj.error}
                            </p>
                          )}
                          {fileObj.uploadId && (
                            <p className="text-xs text-green-600 mt-1">
                              Uploaded (ID: {fileObj.uploadId})
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {fileObj.signed_url && (
                            <button
                              onClick={() => handleViewFile(fileObj)}
                              className="text-indigo-600 hover:text-indigo-800"
                              data-tooltip-id="view-file-tooltip"
                              data-tooltip-content="View file"
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </button>
                          )}
                          {fileObj.uploadId && (
                            <button
                              onClick={() => handleRefreshLink(fileObj, "soa")}
                              className="text-emerald-600 hover:text-emerald-800"
                              data-tooltip-id="view-file-tooltip"
                              data-tooltip-content="Refresh link"
                            >
                              <FontAwesomeIcon icon={faSyncAlt} />
                            </button>
                          )}
                          <button
                            onClick={() => handleRemoveFile(fileObj)}
                            className="text-red-500 hover:text-red-700"
                            disabled={fileObj.isUploading}
                            data-tooltip-id="remove-file-tooltip"
                            data-tooltip-content="Remove file"
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Added/Attached Claims Table */}
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  Added/Attached Claims
                </h3>
                <button
                  onClick={() => setIsAttachedClaimsOpen((prev) => !prev)}
                  className="text-indigo-600 hover:text-indigo-800 flex items-center gap-2"
                  aria-label="Toggle Added/Attached Claims"
                >
                  {isAttachedClaimsOpen ? "Hide" : "Show"} Added/Attached Claims
                  <FontAwesomeIcon
                    icon={isAttachedClaimsOpen ? faChevronUp : faChevronDown}
                  />
                </button>
              </div>
              {isAttachedClaimsOpen && (
                <div className="overflow-x-auto relative">
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon
                        icon={faSearch}
                        className="text-gray-400"
                      />
                      <input
                        value={attachedSearchTerm}
                        onChange={(e) => setAttachedSearchTerm(e.target.value)}
                        placeholder="Search LOA, TRN, Patient, Coverage, Status"
                        className="w-64 max-w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <span>
                        Showing {pagedAttachedClaims.length} of{" "}
                        {filteredAttachedClaims.length}
                      </span>
                      <select
                        value={attachedPerPage}
                        onChange={(e) =>
                          setAttachedPerPage(Number(e.target.value))
                        }
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      >
                        {[5, 10, 20, 50, 100].map((val) => (
                          <option key={val} value={val}>
                            {val} / page
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {isTableLoading && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="text-indigo-600 text-2xl animate-spin"
                      />
                    </div>
                  )}
                  <table className="w-full table-auto">
                    <thead className="bg-gray-50">
                      <tr>
                        {columns.map((column) => (
                          <th
                            key={column.key}
                            className="p-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                          >
                            {column.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredAttachedClaims.length === 0 &&
                      !isTableLoading ? (
                        <tr>
                          <td
                            colSpan={columns.length}
                            className="p-3 text-center text-gray-500 text-sm"
                          >
                            No claims attached.
                          </td>
                        </tr>
                      ) : (
                        pagedAttachedClaims.map((claim) => {
                          const attachmentCount = (claim.attachedFiles || [])
                            .length;
                          const hasAttachments = attachmentCount > 0;

                          return (
                            <tr
                              key={claim.id}
                              className={`border-b ${
                                hasAttachments
                                  ? "bg-emerald-50/70 hover:bg-emerald-100/70"
                                  : "bg-rose-50/60 hover:bg-rose-100/70"
                              }`}
                            >
                              <td className="p-3 text-sm">{claim.loa_no}</td>
                              <td className="p-3 text-sm">
                                {claim.transaction_no}
                              </td>
                              <td className="p-3 text-sm">
                                {phpFormatter.format(claim.amount)}
                              </td>
                              <td className="p-3 text-sm">
                                {claim.claim_amount === null ||
                                claim.claim_amount === undefined ||
                                claim.claim_amount === ""
                                  ? "-"
                                  : phpFormatter.format(claim.claim_amount)}
                              </td>
                              <td className="p-3 text-sm">
                                {claim.patient_name}
                              </td>
                              <td className="p-3 text-sm">{claim.coverage}</td>
                              <td className="p-3 text-sm">{claim.date}</td>
                              <td className="p-3 text-sm">
                                <span
                                  className={`px-2 py-1 rounded-full text-xs ${
                                    claimStatusStyles[claim.claim_status] ||
                                    claimStatusStyles["NULL"]
                                  }`}
                                >
                                  {claim.claim_status || "NULL"}
                                </span>
                              </td>
                              <td className="p-3 text-sm">
                                {hasAttachments ? (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 px-2 py-1 text-xs font-semibold">
                                    <FontAwesomeIcon icon={faCheckCircle} />
                                    Attached
                                    {attachmentCount > 1
                                      ? ` (${attachmentCount})`
                                      : ""}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-700 px-2 py-1 text-xs font-semibold">
                                    <FontAwesomeIcon
                                      icon={faExclamationCircle}
                                    />
                                    Needs Attachment
                                  </span>
                                )}
                              </td>
                              <td className="p-3 text-sm">
                                <div className="flex flex-col gap-1 text-xs">
                                  <span
                                    className={`inline-flex items-center rounded-full px-2.5 py-1 font-semibold ${
                                      claim.stale
                                        ? "bg-rose-100 text-rose-700"
                                        : "bg-emerald-100 text-emerald-700"
                                    }`}
                                  >
                                    {claim.stale ? "Staled" : "No"}
                                  </span>
                                  {claim.rtp_tag && (
                                    <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 font-semibold text-amber-700">
                                      For RTP
                                    </span>
                                  )}
                                  {String(claim.rtp_cancel_status || "").toUpperCase() ===
                                    "PENDING" && (
                                    <span className="inline-flex items-center rounded-full bg-sky-100 px-2.5 py-1 font-semibold text-sky-700">
                                      Cancel Pending
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="p-3">
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    onClick={() => handleViewClaim(claim)}
                                    className="px-3 py-1 text-xs text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 flex items-center gap-1"
                                    data-tooltip-id="view-claim-tooltip"
                                    data-tooltip-content="View claim details"
                                  >
                                    <FontAwesomeIcon icon={faEye} />
                                    View
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleOpenAttachFilesModal(claim)
                                    }
                                    className="px-3 py-1 text-xs text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center gap-1"
                                    data-tooltip-id="attach-files-tooltip"
                                    data-tooltip-content="Attach files to claim"
                                  >
                                    <FontAwesomeIcon icon={faPaperclip} />
                                    Attach Files
                                  </button>
                                  {/* Old RTP action buttons moved to For RTP Entry Module */}
                                  <button
                                    onClick={() => handleScanFiles(claim)}
                                    className="px-3 py-1 text-xs text-white bg-green-600 rounded-lg hover:bg-green-700 flex items-center gap-1"
                                    disabled={
                                      scanning ||
                                      uploadingClaimsSet.has(
                                        claim.transaction_no,
                                      )
                                    }
                                    data-tooltip-id="scan-files-tooltip"
                                    data-tooltip-content="Scan files to attach"
                                  >
                                    <FontAwesomeIcon
                                      icon={
                                        scanning ||
                                        uploadingClaimsSet.has(
                                          claim.transaction_no,
                                        )
                                          ? faSpinner
                                          : faCamera
                                      }
                                      spin={
                                        scanning ||
                                        uploadingClaimsSet.has(
                                          claim.transaction_no,
                                        )
                                      }
                                    />
                                    {scanning
                                      ? "Scanning..."
                                      : uploadingClaimsSet.has(
                                            claim.transaction_no,
                                          )
                                        ? "In Progress Uploading and Attaching..."
                                        : "Scan Files to Attach"}
                                  </button>
                                  <button
                                    onClick={() => handleRemoveClaim(claim)}
                                    className="px-3 py-1 text-xs text-white bg-red-600 rounded-lg hover:bg-red-700 flex items-center gap-1"
                                    data-tooltip-id="remove-claim-tooltip"
                                    data-tooltip-content="Remove claim from batch"
                                  >
                                    <FontAwesomeIcon icon={faTimesCircle} />
                                    Remove
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                  <div className="mt-4 flex flex-wrap items-center justify-end gap-2 text-sm text-gray-600">
                    <button
                      type="button"
                      onClick={() => setAttachedPage(1)}
                      disabled={attachedPage === 1}
                      className="rounded-md border border-gray-200 px-3 py-1 hover:bg-gray-50 disabled:opacity-50"
                    >
                      First
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setAttachedPage((prev) => Math.max(1, prev - 1))
                      }
                      disabled={attachedPage === 1}
                      className="rounded-md border border-gray-200 px-3 py-1 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="px-2">
                      Page {attachedPage} of {attachedTotalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setAttachedPage((prev) =>
                          Math.min(attachedTotalPages, prev + 1),
                        )
                      }
                      disabled={attachedPage === attachedTotalPages}
                      className="rounded-md border border-gray-200 px-3 py-1 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                    <button
                      type="button"
                      onClick={() => setAttachedPage(attachedTotalPages)}
                      disabled={attachedPage === attachedTotalPages}
                      className="rounded-md border border-gray-200 px-3 py-1 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Last
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            {/* <div className="flex justify-end mt-6">
                <button
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                  disabled={
                    isProcessing ||
                    attachedClaims.length === 0 ||
                    files.some((f) => f.isUploading)
                  }
                  data-tooltip-id="submit-batch-tooltip"
                  data-tooltip-content={
                    attachedClaims.length === 0
                      ? "Attach at least one claim to submit"
                      : "Submit batch"
                  }
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                  Submit Batch
                </button>
              </div> */}

            {/* Save and Submit Buttons */}
            <div className="flex justify-end mt-6 gap-3 p-6">
              <button
                onClick={handleSubmit_ForSavingOnly}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                disabled={isProcessing || !isBatchSaved || !formData.batch_no}
                data-tooltip-id="save-open-batch-tooltip"
                data-tooltip-content="Save batch header with status Open"
              >
                <FontAwesomeIcon icon={faSave} />
                Save
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                disabled={
                  isProcessing ||
                  !isBatchSaved ||
                  attachedClaims.length === 0 ||
                  files.some((f) => f.isUploading)
                }
                data-tooltip-id="submit-batch-tooltip"
                data-tooltip-content={
                  !isBatchSaved
                    ? "Save batch header to submit"
                    : attachedClaims.length === 0
                      ? "Attach at least one claim to submit"
                      : "Submit batch"
                }
              >
                <FontAwesomeIcon icon={faPaperPlane} />
                Submit Batch
              </button>
            </div>
          </div>
        </div>

        <CustomScanITReference />

        {/* Old RTP action modal moved to For RTP Entry Module */}

        {/* Loading Modal */}
        {isProcessing && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 flex flex-col items-center space-y-4 animate-pulse">
              <FontAwesomeIcon
                icon={faSpinner}
                className="text-4xl text-indigo-600 animate-spin"
              />
              <p className="text-lg font-medium text-gray-800">
                {isProcessingText}
              </p>
              <p className="text-sm text-gray-500">
                This may take a moment. Please wait.
              </p>
            </div>
          </div>
        )}

        {isWaitingForScan && scanWaitingData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-4">
              <div className="flex items-center gap-3">
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="text-2xl text-indigo-600 animate-spin"
                />
                <div>
                  <p className="text-lg font-semibold text-gray-900">
                    Waiting for scan and attach...
                  </p>
                  <p className="text-sm text-gray-600">
                    Please scan using the Custom Scan App and then click "Save
                    as PDF and Attach to Claims".
                  </p>
                  {wsStatus !== "connected" && (
                    <p className="text-xs font-medium text-amber-600 mt-1">
                      Reconnecting...
                    </p>
                  )}
                </div>
              </div>
              <div className="text-sm text-gray-700 space-y-1">
                {scanWaitingData.type === "soa" ? (
                  <>
                    <p>
                      <span className="font-semibold">SOA No:</span>{" "}
                      {scanWaitingData.soa_no}
                    </p>
                    <p>
                      <span className="font-semibold">Batch No:</span>{" "}
                      {scanWaitingData.batch_no}
                    </p>
                    <p>
                      <span className="font-semibold">Date Received:</span>{" "}
                      {scanWaitingData.date_received}
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      <span className="font-semibold">LOA No:</span>{" "}
                      {scanWaitingData.loa_no}
                    </p>
                    <p>
                      <span className="font-semibold">Transaction No:</span>{" "}
                      {scanWaitingData.transaction_no}
                    </p>
                    <p>
                      <span className="font-semibold">Batch No:</span>{" "}
                      {scanWaitingData.batch_no}
                    </p>
                  </>
                )}
              </div>
              <div className="flex justify-end">
                <button
                  onClick={async () => {
                    const result = await Swal.fire({
                      title: "Cancel scanning?",
                      text: "Are you sure you want to cancel waiting for scan and attach?",
                      icon: "warning",
                      showCancelButton: true,
                      confirmButtonColor: "#ef4444",
                      cancelButtonColor: "#6b7280",
                      confirmButtonText: "Yes, cancel",
                      cancelButtonText: "No",
                    });

                    if (result.isConfirmed) {
                      setIsWaitingForScan(false);
                      setScanWaitingData(null);
                    }
                  }}
                  className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modals */}
        <ClaimDetailsModal
          isOpen={isClaimDetailsModalOpen}
          onClose={() => setIsClaimDetailsModalOpen(false)}
          claim={selectedClaim}
          phpFormatter={phpFormatter}
        />
        <AttachFilesModal
          isOpen={isAttachFilesModalOpen}
          onClose={() => setIsAttachFilesModalOpen(false)}
          claim={selectedClaimForFiles}
          files={claimFiles}
          setFiles={setClaimFiles}
          handleFileChange={handleClaimFileChange}
          handleRemoveFile={handleRemoveClaimFile}
          handleViewFile={handleViewClaimFile}
          handleRefreshLink={handleRefreshLink}
          isDragging={isDraggingClaim}
          handleDragOver={handleClaimDragOver}
          handleDragLeave={handleClaimDragLeave}
          handleDrop={handleClaimDrop}
          handleChooseFile={handleChooseClaimFile}
          fileInputRef={claimFileInputRef}
          allowedFileTypes={allowedFileTypes}
          maxFileSizeMB={maxFileSizeMB}
          maxUploadLimit={maxUploadLimit}
        />
        <ConfirmSubmitModal
          isOpen={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={handleConfirmSubmit}
          formData={formData}
          soaFiles={files}
          attachedClaims={attachedClaims}
          phpFormatter={phpFormatter}
          isSubmitting={isProcessing}
          handleRefreshLink={handleRefreshLink}
        />
      </div>
    </div>
  );
};

export default EditClaimsBatch;
